import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { submitLead } from '../lib/leads';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingBag, Search, X, Plus, Minus, ChevronLeft, ChevronRight, Check,
  Phone, Mail, MessageCircle, Copy, ChevronDown, Package, Wrench, PawPrint,
  ArrowRight, Send, Loader2, ArrowLeft, MapPin, Calendar, Ticket,
} from 'lucide-react';
import { db } from '../Libs/firebase-config.mjs';
import {
  collection, getDocs, addDoc, doc, getDoc, serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

/* ════════════════════════════════════════════════════════════════════════
   CONTACT — single source of truth, used everywhere in this file.
   ════════════════════════════════════════════════════════════════════════ */
const CONTACT = {
  phoneDisplay: '+254 110 563 185',
  phoneHref: 'tel:+254110563185',
  whatsappHref: 'https://wa.me/254110563185',
  email: 'hello@imfuyo.farm',
  emailHref: 'mailto:hello@imfuyo.farm',
};

// Builds a wa.me link pre-filled with a question about a specific item, so
// a shopper can ask before buying without typing the item name themselves.
const whatsappInquiryHref = (itemName) => (
  `${CONTACT.whatsappHref}?text=${encodeURIComponent(`Hi, I have a question about "${itemName}" on the Imfuyo marketplace.`)}`
);

const fmt = (n) => Number(n || 0).toLocaleString();

/* Cheapest applicable price for an item, whether it uses plain pricing,
   the legacy product "options" mechanism, or the newer pricing-tiers
   mechanism (Regular/VIP/VVIP etc.) — used everywhere a card or page needs
   to show a single representative price. */
const cheapestPrice = (item) => {
  if (item.hasTieredPricing && item.pricingTiers?.length) {
    return Math.min(...item.pricingTiers.map((t) => t.price));
  }
  return item.hasOptions ? (item.basePrice ?? item.price ?? 0) : (item.price ?? item.basePrice ?? 0);
};

/* ════════════════════════════════════════════════════════════════════════
   INVENTORY — minimum order quantity + preorder helpers, used by every
   flat-stock item (products/livestock outside of options/tiered pricing,
   which already carry their own per-option/per-tier stock rules). Kept as
   small pure functions so the same logic runs identically on the grid
   cards, the item pages, and the cart's own quantity stepper.
   ════════════════════════════════════════════════════════════════════════ */
const getStock = (item) => Number(item.stock ?? 0);
const getMinOrderQty = (item) => Math.max(1, Number(item.minOrderQuantity) || 1);
// Purchasable if there's stock left, or the shop owner has explicitly
// allowed this item to be ordered as a preorder while stock sits at 0.
const isPurchasable = (item) => getStock(item) > 0 || !!item.allowPreorder;
const isPreorderItem = (item) => getStock(item) <= 0 && !!item.allowPreorder;

/* ════════════════════════════════════════════════════════════════════════
   IMAGES — a single place that decides how an image is fetched and how it
   appears while loading, used by every image in the marketplace:
     - Cloudinary URLs get an automatic-format/automatic-quality/resize
       transformation injected, so the browser downloads a file sized for
       where it's actually displayed instead of the full original upload.
     - Every image lazy-loads by default (the one exception being the main
       photo on an item's own page, which loads eagerly since it's the
       first meaningful thing on that page).
     - Images fade in once decoded instead of popping in abruptly, and the
       surrounding frame keeps its neutral background color the whole
       time, so there's never a blank flash or a layout jump.
   ════════════════════════════════════════════════════════════════════════ */
function optimizeImageUrl(url, width) {
  if (!url || typeof url !== 'string') return url;
  if (url.includes('res.cloudinary.com') && url.includes('/upload/')) {
    return url.replace('/upload/', `/upload/f_auto,q_auto,dpr_auto,w_${width},c_limit/`);
  }
  return url;
}

function SmartImage({ src, alt = '', width = 600, fit = 'contain', priority = false, className = '', onClick }) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setLoaded(false); }, [src]);
  const resolved = src || 'https://via.placeholder.com/400';
  return (
    <img
      src={optimizeImageUrl(resolved, width)}
      alt={alt}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={priority ? 'high' : 'auto'}
      onLoad={() => setLoaded(true)}
      onError={() => setLoaded(true)}
      onClick={onClick}
      className={`mk-smart-img ${loaded ? 'is-loaded' : ''} ${className}`}
      style={{ objectFit: fit }}
    />
  );
}

/* ════════════════════════════════════════════════════════════════════════
   PAYSTACK — redirect checkout via our Firebase Cloud Functions.
   `initializePayment` creates the pending order server-side (recomputing
   prices from Firestore) and returns Paystack's hosted checkout URL; the
   customer is redirected there to pick M-Pesa, card, or bank themselves —
   no phone number collected on our side, no in-app payment UI. Paystack
   redirects back to this same page afterward with `?reference=...`, which
   `Shop` picks up on mount to confirm via `verifyPayment`. The
   `paystackWebhook` function remains the real source of truth and will
   confirm the order even if the customer never makes it back to this page.
   ════════════════════════════════════════════════════════════════════════ */
const functions = getFunctions(); // uses the same default Firebase app as `db`

// initializePayment/verifyPayment MUST be deployed to the same Firebase
// project as `db` above (imfuyobe) — the function's own Firestore admin
// client reads from whatever project it's running in, so if it's deployed
// elsewhere it will look up cart items in the wrong project's Firestore
// and fail with "Item ... no longer exists" even though the call itself
// succeeds. With both sides on the same project, resolving by name here
// builds the correct URL automatically — no hardcoded Cloud Run URL to
// keep updating across redeploys. paystackWebhook is never called from
// the client; it's configured directly as the webhook URL in the
// Paystack dashboard.
const callInitializePayment = httpsCallable(functions, 'initializePayment');
const callVerifyPayment = httpsCallable(functions, 'verifyPayment');

/* Build category filter chips dynamically from whatever data actually
   exists in the db — nothing is hardcoded, so the moment new categories
   are added in Firestore they simply show up here. */
const buildCategoryOptions = (items, labelOverrides = {}) => {
  const cats = [...new Set(items.map((i) => i.category).filter(Boolean))];
  return ['all', ...cats].map((c) => ({
    id: c,
    label: c === 'all' ? 'All' : (labelOverrides[c] || c.charAt(0).toUpperCase() + c.slice(1)),
  }));
};

/* ════════════════════════════════════════════════════════════════════════
   ATOMS
   ════════════════════════════════════════════════════════════════════════ */
/* Names are typed by hand in admin; this only fixes stray commas and double spaces when showing them. */
function cleanName(n) {
  return String(n || '').replace(/\s+,/g, ',').replace(/^(\w+),\s+(?=[a-z])/, '$1 ').replace(/[,;]\s*$/, '').replace(/\s{2,}/g, ' ').trim();
}

function Mono({ children, className = '' }) {
  return <span className={`mk-mono ${className}`}>{children}</span>;
}

function Price({ value, size = 'md', isFree = false, originalPrice = 0 }) {
  const sizes = { xs: '0.72rem', sm: '0.82rem', md: '0.98rem', lg: '1.5rem' };
  if (isFree) {
    return (
      <span className="mk-price mk-price-free" style={{ fontSize: sizes[size] }}>Free</span>
    );
  }
  return (
    <span className="mk-price-wrap">
      <span className="mk-price" style={{ fontSize: sizes[size] }}>
        <span style={{ opacity: 0.5, fontSize: '0.62em', fontWeight: 600, marginRight: 2 }}>KSh</span>
        {fmt(value)}
      </span>
      {originalPrice > value && (
        <span className="mk-price-original">KSh {fmt(originalPrice)}</span>
      )}
    </span>
  );
}

function Pill({ children, tone = 'brand', className = '' }) {
  return <span className={`mk-pill mk-pill-${tone} ${className}`}>{children}</span>;
}

/* Product descriptions are often pasted from social posts: emojis, the page's own link, a price and a phone
   line all run together. This tidies them into a short lead line and a list, without changing the stored text. */
const EMOJI = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE0F}\u{200D}]+/gu;
function tidyDescription(raw, name) {
  const text = String(raw || '').replace(/\r/g, '');
  if (!text.trim()) return { lead: '', items: [] };
  const here = typeof window !== 'undefined' ? window.location.pathname.replace(/\/$/, '').toLowerCase() : '';
  let parts = text.replace(EMOJI, '\n').split(/\n+/).map((x) => x.trim()).filter(Boolean);
  parts = parts.filter((x) => {
    if (/^(ksh|kes|kenya shillings?)\.?\s*[\d,]+(\.\d+)?$/i.test(x)) return false;                 // price is already shown
    if (/^(buy here|order here|shop here)\s*:?/i.test(x) && /imfuyo\.farm\/shop/i.test(x)) return false; // link to this very page
    if (/^whatsapp\s*:/i.test(x)) return false;                                                       // page has its own WhatsApp link
    if (here && x.toLowerCase().replace(/\/$/, '').endsWith(here) && x.length < here.length + 40) return false;
    return true;
  });
  if (!parts.length) return { lead: '', items: [] };
  let lead = parts[0];
  // The first line often repeats the product name, for example "MbuziPlus Course – Dairy Goat Farming Learn ...".
  const nm = String(name || '').trim();
  if (nm && lead.toLowerCase().startsWith(nm.toLowerCase())) {
    lead = lead.slice(nm.length).replace(/^[\s\-–—:]+/, '');
    const m = lead.match(/^(?:[A-Z][a-z]+\s+){1,4}?(?=[A-Z][a-z]+\s+[a-z])/);   // drop a leftover title such as "Dairy Goat Farming"
    if (m) lead = lead.slice(m[0].length);
  }
  lead = lead.trim();
  let rest = parts.slice(1);
  // A leftover subtitle such as "Dairy Goat Farming" is not a sentence: use the next line as the lead instead.
  if (rest.length && lead.split(/\s+/).length <= 5 && !/[.!?]$/.test(lead)) { lead = rest[0]; rest = rest.slice(1); }
  return { lead, items: rest };
}

function linkify(str) {
  return String(str).split(/(https?:\/\/[^\s]+|[\w.+-]+@[\w-]+\.[\w.]+)/g).map((seg, i) => {
    if (/^https?:\/\//.test(seg)) {
      const clean = seg.replace(/[).,]+$/, '');
      const academy = /imfuyo\.farm\/academy/i.test(clean);
      return <a key={i} href={clean} target={academy ? undefined : '_blank'} rel="noopener noreferrer" className="mk-desc-link">{academy ? 'imfuyo.farm/academy' : clean.replace(/^https?:\/\//, '')}</a>;
    }
    if (/^[\w.+-]+@/.test(seg)) return <a key={i} href={`mailto:${seg}`} className="mk-desc-link">{seg}</a>;
    return seg;
  });
}

const COURSE_STEPS = [
  ['Add to cart and pay', 'Pay Kenya Shillings 4,900 on the secure Paystack page with M-Pesa, card or bank. This is the full training fee, with no other training cost. Farm visits are charged separately.'],
  ['Receive your access key', 'The Imfuyo team sends your personal access key on WhatsApp, usually within 12 to 24 hours, with the dates of the next training.'],
  ['Learn online or in class', 'Use the key at imfuyo.farm/academy for the course, with 8 parts and 42 modules. Join the first and second sessions online or in class, virtual or physical. A free online webinar (10 a.m. to 12 p.m.) is held on the third Saturday of every month: reserve your seat under Events in this Marketplace.'],
];

function Description({ text, name }) {
  const isCourse = /mbuziplus course/i.test(name || '');
  const tidy = tidyDescription(text, name);
  const lead = tidy.lead;
  // Split the pasted lines into: ordinary points, delivery wording and Paybill payment details, each shown once and clearly.
  let delivery = false;
  const pay = { paybill: '', account: '', name: '' };
  const groups = [];
  (isCourse ? [] : tidy.items).forEach((x) => {
    const t = x.trim();
    if (/^minimum order/i.test(t)) return;
    if (/^payment details/i.test(t)) return;
    if (/paybill/i.test(t)) { pay.paybill = (t.match(/\d{4,}/) || [''])[0]; return; }
    if (/account\s*(number|no)/i.test(t)) { pay.account = (t.match(/\d{4,}/) || [''])[0]; return; }
    if (/bank/i.test(t) && /imfuyo/i.test(t)) { pay.name = t.split(/\s*[:–—-]\s*/).slice(1).join(' - ') || t; return; }
    if (/^delivery/i.test(t) || (/deliver/i.test(t) && /fees?/i.test(t))) { delivery = true; return; }
    if (/:$/.test(t)) { groups.push({ head: t.replace(/:$/, ''), items: [] }); return; }
    if (!groups.length) groups.push({ head: '', items: [] });
    groups[groups.length - 1].items.push(t);
  });
  const hasPay = pay.paybill && pay.account;
  if (!lead && !groups.length && !isCourse && !delivery && !hasPay) return null;
  return (
    <div className="mk-desc">
      {lead && <p className="mk-desc-lead">{linkify(lead)}</p>}
      {isCourse && (
        <>
          <p className="mk-desc-sub">What you get for Kenya Shillings 4,900</p>
          <ul className="mk-desc-list">
            <li>The full MbuziPlus Course online, with 8 parts and 42 modules</li>
            <li>The first and second training sessions, online or in class</li>
            <li>Your Certificate of Completion</li>
            <li>Access to the Imfuyo alumni community</li>
          </ul>
          <p className="mk-desc-sub">How it works</p>
          <ol className="mk-desc-steps">
            {COURSE_STEPS.map(([t, d], i) => (
              <li key={t}><span className="mk-desc-n">{i + 1}</span><span><strong>{t}.</strong> {d}</span></li>
            ))}
          </ol>
          <p className="mk-desc-note">One access key is for one student and one device. For a group of five or more, ask us about a group key on WhatsApp.</p>
        </>
      )}
      {groups.map((g, gi) => (
        <div key={gi}>
          {g.head && <p className="mk-desc-sub">{g.head}</p>}
          {g.items.length > 0 && <ul className="mk-desc-list">{g.items.map((x, i) => <li key={i}>{linkify(x)}</li>)}</ul>}
        </div>
      ))}
      {delivery && <p className="mk-desc-note"><strong>Delivery:</strong> we deliver countrywide. The delivery fee and the number of days depend on where you are.</p>}
      {hasPay && (
        <div className="mk-paybox">
          <strong>Cannot pay at checkout? Pay by M-Pesa Paybill instead</strong>
          <span>Paybill number: <b>{pay.paybill}</b></span>
          <span>Account number: <b>{pay.account}</b></span>
          {pay.name && <span>Account name: <b>{pay.name}</b></span>}
          <span>After paying, WhatsApp the M-Pesa confirmation message and the product name to {CONTACT.phoneDisplay} so we can match it to your order.</span>
        </div>
      )}
    </div>
  );
}

function Eyebrow({ children }) {
  return <span className="mk-eyebrow">{children}</span>;
}

function IconButton({ icon: Icon, onClick, label, size = 36, subtle = false, style }) {
  return (
    <button onClick={onClick} aria-label={label} className={`mk-iconbtn ${subtle ? 'is-subtle' : ''}`} style={{ width: size, height: size, ...style }}>
      <Icon size={Math.round(size * 0.42)} />
    </button>
  );
}

function Stepper({ value, onDecrement, onIncrement, disabledDecrement, disabledIncrement, small }) {
  return (
    <div className={`mk-stepper ${small ? 'is-small' : ''}`}>
      <button onClick={onDecrement} disabled={disabledDecrement} aria-label="Decrease quantity"><Minus size={small ? 11 : 13} /></button>
      <Mono className="mk-stepper-value">{value}</Mono>
      <button onClick={onIncrement} disabled={disabledIncrement} aria-label="Increase quantity"><Plus size={small ? 11 : 13} /></button>
    </div>
  );
}

function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="mk-empty">
      <div className="mk-empty-mark"><Icon size={22} /></div>
      <p className="mk-empty-title">{title}</p>
      {subtitle && <p className="mk-empty-sub">{subtitle}</p>}
    </div>
  );
}

function SkeletonGrid({ count = 6 }) {
  return (
    <div className="mk-grid">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="mk-skel-item">
          <div className="mk-skel-img" />
          <div className="mk-skel-lines">
            <div className="mk-skel-line" style={{ width: '72%' }} />
            <div className="mk-skel-line" style={{ width: '38%', marginTop: 8 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Toast({ message }) {
  return (
    <AnimatePresence>
      {message && (
        <motion.div
          initial={{ opacity: 0, y: 10, x: '-50%' }}
          animate={{ opacity: 1, y: 0, x: '-50%' }}
          exit={{ opacity: 0, y: 10, x: '-50%' }}
          transition={{ duration: 0.2 }}
          className="mk-toast"
        >
          <span className="mk-toast-mark"><Check size={12} /></span>
          {message}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   HEADER CONTROLS
   ════════════════════════════════════════════════════════════════════════ */
function SegmentedTabs({ tabs, active, onChange }) {
  return (
    <div className="mk-segmented no-scrollbar" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={active === t.id}
          onClick={() => onChange(t.id)}
          className={`mk-segment ${active === t.id ? 'is-active' : ''}`}
        >
          {active === t.id && <motion.span layoutId="mk-segment-bg" className="mk-segment-bg" transition={{ type: 'spring', damping: 28, stiffness: 300 }} />}
          <t.icon size={14} className="mk-segment-icon" />
          <span>{t.label}</span>
        </button>
      ))}
    </div>
  );
}

function FilterChips({ options, active, onChange }) {
  if (options.length <= 1) return null;
  return (
    <div className="mk-chiprow no-scrollbar">
      {options.map((opt) => (
        <button key={opt.id} onClick={() => onChange(opt.id)} className={`mk-chip ${active === opt.id ? 'is-active' : ''}`}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function SearchExpand({ value, onChange, placeholder, open, setOpen }) {
  const inputRef = useRef(null);
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  return (
    <div className="mk-searchwrap">
      <button className="mk-search-toggle" onClick={() => setOpen((o) => !o)} aria-label="Toggle search">
        <Search size={16} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 'min(340px, 62vw)', opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="mk-search-field"
          >
            <input ref={inputRef} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
            {value && (
              <button onClick={() => onChange('')} className="mk-search-clear" aria-label="Clear search"><X size={12} /></button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   SORT CONTROL — price / newest, chosen from the header row next to search.
   Kept intentionally small: only the axes that make sense for a farm
   marketplace, not a generic sort-everything dropdown.
   ════════════════════════════════════════════════════════════════════════ */
function SortSelect({ value, onChange }) {
  return (
    <div className="mk-sortwrap">
      <select className="mk-sort" value={value} onChange={(e) => onChange(e.target.value)} aria-label="Sort by">
        <option value="default">Sort</option>
        <option value="price-asc">Price: low to high</option>
        <option value="price-desc">Price: high to low</option>
        <option value="newest">Newest first</option>
      </select>
      <ChevronDown size={13} className="mk-sort-caret" />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   RESPONSIVE HELPER — detects mobile viewport so modals can switch from a
   centered popup (desktop) to a sliding bottom sheet (mobile).
   ════════════════════════════════════════════════════════════════════════ */
function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  ));
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);
    handler(mq);
    if (mq.addEventListener) mq.addEventListener('change', handler); else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler); else mq.removeListener(handler);
    };
  }, [breakpoint]);
  return isMobile;
}

/* ════════════════════════════════════════════════════════════════════════
   CENTER MODAL — desktop: centered popup. Mobile: bottom sheet that slides
   up from the bottom edge, the modern mobile pattern instead of a floating
   popup. Still used for short-lived flows: cart, checkout, the type
   picker, and the service application form — not for full item views,
   which now live on their own page (see the *Page components below).
   ════════════════════════════════════════════════════════════════════════ */
function CenterModal({ onClose, title, children, footer, maxWidth = 460 }) {
  const isMobile = useIsMobile();
  return (
    <motion.div className="mk-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div
        initial={isMobile ? { y: '100%' } : { scale: 0.92, opacity: 0, y: 14 }}
        animate={isMobile ? { y: 0 } : { scale: 1, opacity: 1, y: 0 }}
        exit={isMobile ? { y: '100%' } : { scale: 0.95, opacity: 0, y: 8 }}
        transition={isMobile ? { type: 'spring', damping: 32, stiffness: 320 } : { type: 'spring', damping: 30, stiffness: 340 }}
        onClick={(e) => e.stopPropagation()}
        className={`mk-modal ${isMobile ? 'mk-modal-sheet' : ''}`}
        style={isMobile ? undefined : { maxWidth }}
      >
        {isMobile && <div className="mk-sheet-handle" />}
        <div className="mk-modal-header">
          <h3 className="mk-heading">{title}</h3>
          <IconButton icon={X} onClick={onClose} label="Close" subtle size={32} />
        </div>
        <div className="mk-modal-body">{children}</div>
        {footer && <div className="mk-modal-footer">{footer}</div>}
      </motion.div>
    </motion.div>
  );
}

/* Gallery with a main frame + thumbnail rail. Images are always shown in
   full (object-fit: contain) — nothing is ever cropped to fill the frame.
   The main frame loads eagerly (it's the first meaningful image on an item
   page); thumbnails and every other image on the page load lazily. */
function Gallery({ images }) {
  const [i, setI] = useState(0);
  const list = images && images.length ? images : ['https://via.placeholder.com/600'];
  return (
    <div className="mk-gallery">
      <div className="mk-gallery-frame">
        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="mk-gallery-frame-inner"
          >
            <SmartImage src={list[i]} alt="" width={900} fit="contain" priority={i === 0} />
          </motion.div>
        </AnimatePresence>
        {list.length > 1 && (
          <>
            <button className="mk-gallery-nav prev" onClick={() => setI((v) => (v === 0 ? list.length - 1 : v - 1))} aria-label="Previous image"><ChevronLeft size={16} /></button>
            <button className="mk-gallery-nav next" onClick={() => setI((v) => (v + 1) % list.length)} aria-label="Next image"><ChevronRight size={16} /></button>
            <Mono className="mk-gallery-count">{i + 1} / {list.length}</Mono>
          </>
        )}
      </div>
      {list.length > 1 && (
        <div className="mk-gallery-rail no-scrollbar">
          {list.map((src, idx) => (
            <button key={idx} onClick={() => setI(idx)} className={`mk-thumb ${idx === i ? 'is-active' : ''}`} aria-label={`Image ${idx + 1}`}>
              <SmartImage src={src} alt="" width={120} fit="cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   ITEM ENTRIES — plain, uncontained entries laid directly on the page
   (no card chrome: no border, no background box, no shadow). Images are
   shown in full with object-fit: contain, never cropped, sitting inside a
   softly inset frame so the product/animal reads clearly against a
   consistent neutral background instead of touching the card edges.
   ════════════════════════════════════════════════════════════════════════ */
function MarketCard({ item, badge, onOpen, onQuickAdd }) {
  const priceValue = cheapestPrice(item);
  const showFrom = item.hasOptions || (item.hasTieredPricing && item.pricingTiers?.length > 1);
  // Flat-stock items (no options, no pricing tiers) are the only ones this
  // card enforces stock/preorder rules for directly — options and tiers
  // carry their own per-option/per-tier stock and are resolved on the
  // item's own page instead.
  const isFlatStock = !item.hasOptions && !(item.hasTieredPricing && item.pricingTiers?.length);
  const soldOut = isFlatStock && !isPurchasable(item);
  const preorder = isFlatStock && isPreorderItem(item);
  const hasChoice = item.hasOptions || (item.hasTieredPricing && item.pricingTiers?.length);
  const actionLabel = soldOut ? 'Sold out' : hasChoice ? 'Choose type' : preorder ? 'Preorder' : 'Add to cart';
  return (
    <div className={`mk-item ${soldOut ? 'is-closed' : ''}`} onClick={onOpen}>
      <div className="mk-item-img mk-item-img-contain">
        <SmartImage src={item.images?.[0]} width={500} fit="contain" alt={item.name} />
        {badge && <Pill tone="brand">{badge}</Pill>}
        {item.isFree ? (
          <Pill tone="brand" className="mk-pill-right">Free</Pill>
        ) : item.onOffer ? (
          <Pill tone="amber" className="mk-pill-right">Offer</Pill>
        ) : soldOut ? (
          <Pill tone="amber" className="mk-pill-right">Sold out</Pill>
        ) : null}
      </div>
      <div className="mk-item-body">
        <h3 className="mk-item-title">{cleanName(item.name)}</h3>
        <div className="mk-item-priceline">
          {showFrom && !item.isFree && <span className="mk-from">From</span>}
          <Price value={priceValue} isFree={item.isFree} originalPrice={item.onOffer ? item.originalPrice : 0} size="md" />
          {item.unit ? <span className="mk-item-unit">{item.unit}</span> : null}
        </div>
        <button
          className="mk-card-btn"
          disabled={soldOut}
          onClick={(e) => { e.stopPropagation(); if (!soldOut) onQuickAdd(); }}
        >
          {actionLabel}
        </button>
      </div>
    </div>
  );
}

function ServiceCard({ service, onOpen }) {
  const priceValue = cheapestPrice(service);
  const showFrom = service.hasTieredPricing && service.pricingTiers?.length > 1;
  return (
    <div className="mk-item mk-service-item" onClick={onOpen}>
      <div className="mk-item-img mk-service-img">
        <SmartImage src={service.images?.[0]} width={700} fit="cover" alt={service.name} />
        {service.category && <Pill tone="amber">{service.category}</Pill>}
        {service.isFree && <Pill tone="brand" className="mk-pill-right">Free</Pill>}
        {!service.isFree && service.onOffer && <Pill tone="amber" className="mk-pill-right">Offer</Pill>}
      </div>
      <div className="mk-item-body">
        <h3 className="mk-item-title">{service.name}</h3>
        <p className="mk-item-desc">{service.description}</p>
        <div className="mk-item-row">
          {priceValue > 0 || service.isFree ? (
            <div>
              {showFrom && !service.isFree && <span className="mk-from">from</span>}
              <Price value={priceValue} isFree={service.isFree} originalPrice={service.onOffer ? service.originalPrice : 0} />
            </div>
          ) : <span />}
          <span className="mk-card-link">Learn more <ChevronRight size={13} /></span>
        </div>
      </div>
    </div>
  );
}

/* Event cards surface the date and location up front, since — unlike a
   product — when it happens is as important as what it costs. */
function EventCard({ event, onOpen }) {
  const priceValue = cheapestPrice(event);
  const showFrom = event.hasTieredPricing && event.pricingTiers?.length > 1;
  const isClosed = event.status === 'past' || event.status === 'cancelled';
  const dateLabel = event.eventDate
    ? new Date(event.eventDate).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <div className={`mk-item mk-service-item ${isClosed ? 'is-closed' : ''}`} onClick={onOpen}>
      <div className="mk-item-img mk-service-img">
        <SmartImage src={event.images?.[0]} width={700} fit="cover" alt={event.name} />
        {event.category && <Pill tone="amber">{event.category}</Pill>}
        {isClosed ? (
          <Pill tone="amber" className="mk-pill-right">{event.status === 'cancelled' ? 'Cancelled' : 'Past'}</Pill>
        ) : (
          <>
            {event.isFree && <Pill tone="brand" className="mk-pill-right">Free</Pill>}
            {!event.isFree && event.onOffer && <Pill tone="amber" className="mk-pill-right">Offer</Pill>}
          </>
        )}
      </div>
      <div className="mk-item-body">
        <h3 className="mk-item-title">{event.name}</h3>
        {(dateLabel || event.location) && (
          <p className="mk-event-card-meta">
            {dateLabel && <span><Calendar size={12} /> {dateLabel}</span>}
            {event.location && <span><MapPin size={12} /> {event.location}</span>}
          </p>
        )}
        <p className="mk-item-desc">{event.description}</p>
        <div className="mk-item-row">
          {priceValue > 0 || event.isFree ? (
            <div>
              {showFrom && !event.isFree && <span className="mk-from">from</span>}
              <Price value={priceValue} isFree={event.isFree} originalPrice={event.onOffer ? event.originalPrice : 0} />
            </div>
          ) : <span />}
          <span className="mk-card-link">View details <ChevronRight size={13} /></span>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   OPTION SWATCHES — legacy product "type" picker (e.g. different animal
   types on an identification-tag product). Unrelated to pricing tiers.
   ════════════════════════════════════════════════════════════════════════ */
function OptionSwatch({ opt, qty, onChange, unit, minQty = 1 }) {
  const selected = qty > 0;
  return (
    <div className={`mk-swatch ${selected ? 'is-selected' : ''}`}>
      <button className="mk-swatch-main" onClick={() => onChange(qty === 0 ? minQty : qty)}>
        <span className="mk-swatch-icon"><PawPrint size={15} /></span>
        <span className="mk-swatch-text">
          <span className="mk-swatch-name">{opt.name}</span>
          <span className="mk-swatch-desc">{opt.description}</span>
        </span>
        {selected && <span className="mk-swatch-check"><Check size={11} /></span>}
      </button>
      <div className="mk-swatch-foot">
        <Price value={opt.price} size="sm" />
        <span className="mk-swatch-unit">{unit}</span>
        {selected && (
          <div className="mk-swatch-stepper-wrap">
            <Stepper small value={qty} onDecrement={() => onChange(qty <= minQty ? 0 : qty - 1)} onIncrement={() => onChange(qty + 1)} disabledDecrement={qty <= minQty} />
          </div>
        )}
      </div>
    </div>
  );
}

function OptionPickerModal({ product, qty, onChange, onClose }) {
  const totalItems = Object.values(qty).reduce((s, n) => s + n, 0);
  const minQty = getMinOrderQty(product);
  return (
    <CenterModal
      onClose={onClose}
      title="Select type"
      maxWidth={440}
      footer={<button className="mk-btn-primary" onClick={onClose}>{totalItems > 0 ? `Done — ${totalItems} selected` : 'Close'}</button>}
    >
      {minQty > 1 && <p className="mk-note" style={{ marginTop: 0 }}>Minimum order per type selected: {minQty}.</p>}
      <div className="mk-swatch-grid">
        {product.options.map((opt) => (
          <OptionSwatch key={opt.id} opt={opt} unit={product.unit} qty={qty[opt.id] || 0} onChange={(n) => onChange(opt.id, n)} minQty={minQty} />
        ))}
      </div>
    </CenterModal>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   PRICING TIERS — the shared "Regular / VIP / VVIP" style picker, used
   across products (without options), livestock, and events. Each tier can
   have its own stock cap; once reached the stepper stops incrementing and
   a sold-out state disables picking it altogether.
   ════════════════════════════════════════════════════════════════════════ */
function TierSelector({ tiers, quantities, onChange, minQty = 1 }) {
  return (
    <div className="mk-swatch-grid">
      {tiers.map((t) => {
        const qty = quantities[t.id] || 0;
        const soldOut = t.stock === 0;
        const atCap = t.stock != null && qty >= t.stock;
        return (
          <div key={t.id} className={`mk-swatch ${qty > 0 ? 'is-selected' : ''} ${soldOut ? 'is-disabled' : ''}`}>
            <button className="mk-swatch-main" disabled={soldOut} onClick={() => onChange(t.id, qty === 0 ? minQty : qty)}>
              <span className="mk-swatch-icon"><Ticket size={15} /></span>
              <span className="mk-swatch-text">
                <span className="mk-swatch-name">{t.name}</span>
                {t.description && <span className="mk-swatch-desc">{t.description}</span>}
                {t.stock != null && (
                  <span className="mk-swatch-desc mk-swatch-stock">{soldOut ? 'Sold out' : `${t.stock} left`}</span>
                )}
              </span>
              {qty > 0 && !soldOut && <span className="mk-swatch-check"><Check size={11} /></span>}
            </button>
            <div className="mk-swatch-foot">
              <Price value={t.price} size="sm" originalPrice={t.originalPrice > t.price ? t.originalPrice : 0} />
              {qty > 0 && !soldOut && (
                <div className="mk-swatch-stepper-wrap">
                  <Stepper
                    small
                    value={qty}
                    onDecrement={() => onChange(t.id, qty <= minQty ? 0 : qty - 1)}
                    onIncrement={() => onChange(t.id, atCap ? qty : qty + 1)}
                    disabledDecrement={qty <= minQty}
                    disabledIncrement={atCap}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* Read-only pricing-tier list — used on the Service page, where booking
   happens through the application form rather than a cart, so tiers are
   informational (e.g. "Standard visit / Priority visit") rather than
   something added to cart directly. */
function PricingTierList({ tiers }) {
  return (
    <div className="mk-tier-list">
      {tiers.map((t) => (
        <div key={t.id} className="mk-tier-row">
          <div className="mk-tier-text">
            <span className="mk-tier-name">{t.name}</span>
            {t.description && <span className="mk-tier-desc">{t.description}</span>}
          </div>
          <Price value={t.price} size="sm" originalPrice={t.originalPrice > t.price ? t.originalPrice : 0} />
        </div>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   STICKY MOBILE CTA BAR — pinned to the bottom of the viewport on item
   pages (product/livestock/event) while on mobile, so the price + action
   is always reachable without scrolling back up a long page. Hidden on
   desktop, where the CTA already sits inline near the top.
   ════════════════════════════════════════════════════════════════════════ */
function StickyMobileCta({ price, isFree, label, disabled, onClick }) {
  return (
    <div className="mk-sticky-cta">
      <div className="mk-sticky-cta-price">
        <span className="mk-muted" style={{ fontSize: '0.65rem', display: 'block' }}>Total</span>
        <Price value={price} isFree={isFree} size="md" />
      </div>
      <button className="mk-btn-primary mk-sticky-cta-btn" disabled={disabled} onClick={onClick}>
        <ShoppingBag size={15} /> {label}
      </button>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   PRODUCT / LIVESTOCK / SERVICE / EVENT PAGES — each item opens on its own
   full page (in place of the grid) instead of a popup. The hero image sits
   directly in the page flow, full-bleed, rather than inside a distinct
   boxed/card frame.
   ════════════════════════════════════════════════════════════════════════ */
function ProductPage({ product, onBack, onAddToCart, onBuyNow }) {
  const [qty, setQty] = useState({});
  const [tierQty, setTierQty] = useState({});
  const [showOptionModal, setShowOptionModal] = useState(false);

  // A product can use the legacy "type" options (hasOptions) or the newer
  // pricing tiers (hasTieredPricing) — not both at once. Options win if a
  // product happens to have both set, since that mechanism also carries a
  // distinct animal-type identity per option, not just a price.
  const hasTiers = !product.hasOptions && product.hasTieredPricing && product.pricingTiers?.length > 0;
  const minQty = getMinOrderQty(product);
  // Only the plain flat-price path (no options, no tiers) is governed by
  // the product's own top-level stock/preorder flags — options and tiers
  // resolve availability per-option/per-tier instead.
  const isFlatStock = !product.hasOptions && !hasTiers;
  const flatAvailable = !isFlatStock || isPurchasable(product);
  const flatPreorder = isFlatStock && isPreorderItem(product);

  const isCourse = /mbuziplus course/i.test(product.name || '');
  const totalItems = useMemo(() => Object.values(qty).reduce((s, n) => s + n, 0), [qty]);
  const tierTotalItems = useMemo(() => Object.values(tierQty).reduce((s, n) => s + n, 0), [tierQty]);

  const totalPrice = useMemo(() => {
    if (product.hasOptions) {
      return Object.entries(qty).reduce((sum, [id, n]) => {
        const opt = product.options.find((o) => o.id === id);
        return sum + (opt ? opt.price * n : 0);
      }, 0);
    }
    if (hasTiers) {
      return product.pricingTiers.reduce((sum, t) => sum + (tierQty[t.id] || 0) * t.price, 0);
    }
    return product.price;
  }, [qty, tierQty, product, hasTiers]);

  const setOptQty = (id, n) => setQty((prev) => {
    const copy = { ...prev };
    if (n <= 0) delete copy[id]; else copy[id] = n;
    return copy;
  });
  const setTierQtyValue = (id, n) => setTierQty((prev) => {
    const copy = { ...prev };
    if (n <= 0) delete copy[id]; else copy[id] = n;
    return copy;
  });

  const handleAdd = () => {
    if (product.hasOptions) {
      if (totalItems === 0) return;
      Object.entries(qty).forEach(([id, n]) => {
        const opt = product.options.find((o) => o.id === id);
        onAddToCart({ ...product, price: opt.price, animalType: opt.name, optionId: opt.id }, 'product', n);
      });
    } else if (hasTiers) {
      if (tierTotalItems === 0) return;
      product.pricingTiers.forEach((t) => {
        const n = tierQty[t.id] || 0;
        if (n > 0) onAddToCart({ ...product, price: t.price, tierName: t.name, tierId: t.id }, 'product', n);
      });
    } else {
      if (!flatAvailable) return;
      if (isCourse && onBuyNow) onBuyNow({ ...product, isPreorder: flatPreorder }, 'product', minQty);
      else onAddToCart({ ...product, isPreorder: flatPreorder }, 'product', minQty);
    }
  };

  const ctaDisabled = (product.hasOptions && totalItems === 0) || (hasTiers && tierTotalItems === 0) || (isFlatStock && !flatAvailable);
  const ctaLabel = product.hasOptions
    ? (totalItems > 0 ? `Add ${totalItems} to cart` : 'Select a type first')
    : hasTiers
      ? (tierTotalItems > 0 ? `Add ${tierTotalItems} to cart` : 'Select an option')
      : (!flatAvailable ? 'Out of stock' : flatPreorder ? `Preorder ${minQty > 1 ? `(min ${minQty})` : ''}`.trim() : (isCourse ? 'Register and pay now' : minQty > 1 ? `Add ${minQty} to cart` : 'Add to cart'));

  return (
    <div className="mk-page">
      <button className="mk-page-back" onClick={onBack}><ArrowLeft size={16} /> Back to products</button>

      <div className="mk-page-layout">
      <div className="mk-page-media"><Gallery images={product.images} /></div>
      <div className="mk-page-info">
      <div className="mk-page-body">
        {(product.isFree || product.onOffer || flatPreorder) && (
          <div className="mk-tagrow">
            {product.isFree && <Pill tone="brand">Free</Pill>}
            {!product.isFree && product.onOffer && <Pill tone="amber">On offer</Pill>}
            {flatPreorder && <Pill tone="amber">Preorder</Pill>}
          </div>
        )}

        <h1 className="mk-page-title">{cleanName(product.name)}</h1>

        <div className="mk-page-price-row">
          {(product.hasOptions || hasTiers) && totalPrice === 0 && <span className="mk-from" style={{ margin: 0 }}>From</span>}
          <Price
            value={product.hasOptions || hasTiers ? (totalPrice || cheapestPrice(product)) : product.price}
            size="lg"
            isFree={!product.hasOptions && !hasTiers && product.isFree}
            originalPrice={!product.hasOptions && !hasTiers && product.onOffer ? product.originalPrice : 0}
          />
          {(product.hasOptions || hasTiers ? totalPrice === 0 : true) && product.unit ? <span className="mk-card-unit">{String(product.unit).toLowerCase()}</span> : null}
        </div>

        {isFlatStock && minQty > 1 && flatAvailable && (
          <p className="mk-note" style={{ marginTop: 0 }}>Minimum order: {minQty} {String(product.unit || 'units').replace(/^per\s+/i, '')}s, which comes to KSh {fmt(product.price * minQty)}.</p>
        )}
        {flatPreorder && (
          <p className="mk-note" style={{ marginTop: 0 }}>Currently out of stock — this item ships as a preorder and we'll confirm timing with you after checkout.</p>
        )}

        <Description text={product.description} name={product.name} />

        {product.hasOptions ? (
          <div className="mk-options">
            <Eyebrow>type &amp; quantity</Eyebrow>
            <div className="mk-opt-summary">
              {totalItems === 0 ? (
                <p className="mk-opt-summary-empty">No type selected yet.</p>
              ) : (
                <div className="mk-opt-chip-row">
                  {Object.entries(qty).map(([id, n]) => {
                    const opt = product.options.find((o) => o.id === id);
                    return opt ? <span key={id} className="mk-opt-chip">{opt.name} × {n}</span> : null;
                  })}
                </div>
              )}
              <button className="mk-opt-select-btn" onClick={() => setShowOptionModal(true)}>
                <PawPrint size={14} /> {totalItems > 0 ? 'Change selection' : 'Select type & quantity'}
              </button>
            </div>
          </div>
        ) : hasTiers ? (
          <div className="mk-options">
            <Eyebrow>choose an option</Eyebrow>
            <TierSelector tiers={product.pricingTiers} quantities={tierQty} onChange={setTierQtyValue} minQty={minQty} />
          </div>
        ) : (
          null
        )}

        <button className="mk-btn-primary mk-page-cta" disabled={ctaDisabled} onClick={handleAdd}>
          <ShoppingBag size={16} /> {ctaLabel}
        </button>

        {!/mbuziplus course/i.test(product.name || '') && <p className="mk-next">What happens next: you pay on the secure Paystack page (M-Pesa, card or bank) and see your confirmation straight away. Our team then contacts you on WhatsApp within 24 hours to arrange delivery or collection.</p>}

        <a href={whatsappInquiryHref(product.name)} target="_blank" rel="noopener noreferrer" className="mk-inquire-link">
          <MessageCircle size={14} /> Ask a question about this on WhatsApp
        </a>
      </div>
      </div>
      </div>

      <AnimatePresence>
        {showOptionModal && (
          <OptionPickerModal product={product} qty={qty} onChange={setOptQty} onClose={() => setShowOptionModal(false)} />
        )}
      </AnimatePresence>

      <StickyMobileCta
        price={product.hasOptions || hasTiers ? (totalPrice || cheapestPrice(product)) : product.price * (isFlatStock ? minQty : 1)}
        isFree={!product.hasOptions && !hasTiers && product.isFree}
        label={ctaLabel}
        disabled={ctaDisabled}
        onClick={handleAdd}
      />
    </div>
  );
}

function LivestockPage({ animal, onBack, onAddToCart }) {
  const [tierQty, setTierQty] = useState({});
  const hasTiers = animal.hasTieredPricing && animal.pricingTiers?.length > 0;
  const minQty = getMinOrderQty(animal);
  const isFlatStock = !hasTiers;
  const flatAvailable = !isFlatStock || isPurchasable(animal);
  const flatPreorder = isFlatStock && isPreorderItem(animal);

  const tierTotalItems = useMemo(() => Object.values(tierQty).reduce((s, n) => s + n, 0), [tierQty]);
  const totalPrice = useMemo(() => {
    if (!hasTiers) return animal.price;
    return animal.pricingTiers.reduce((sum, t) => sum + (tierQty[t.id] || 0) * t.price, 0);
  }, [tierQty, animal, hasTiers]);

  const setTierQtyValue = (id, n) => setTierQty((prev) => {
    const copy = { ...prev };
    if (n <= 0) delete copy[id]; else copy[id] = n;
    return copy;
  });

  const handleAdd = () => {
    if (hasTiers) {
      if (tierTotalItems === 0) return;
      animal.pricingTiers.forEach((t) => {
        const n = tierQty[t.id] || 0;
        if (n > 0) onAddToCart({ ...animal, price: t.price, tierName: t.name, tierId: t.id }, 'livestock', n);
      });
    } else {
      if (!flatAvailable) return;
      onAddToCart({ ...animal, isPreorder: flatPreorder }, 'livestock', minQty);
    }
  };

  const ctaDisabled = (hasTiers && tierTotalItems === 0) || (isFlatStock && !flatAvailable);
  const ctaLabel = hasTiers
    ? (tierTotalItems > 0 ? `Add ${tierTotalItems} to cart` : 'Select an option')
    : (!flatAvailable ? 'Out of stock' : flatPreorder ? `Preorder ${minQty > 1 ? `(min ${minQty})` : ''}`.trim() : (minQty > 1 ? `Add ${minQty} to cart` : 'Add to cart'));

  return (
    <div className="mk-page">
      <button className="mk-page-back" onClick={onBack}><ArrowLeft size={16} /> Back to livestock</button>

      <div className="mk-page-layout">
      <div className="mk-page-media"><Gallery images={animal.images} /></div>
      <div className="mk-page-info">
      <div className="mk-page-body">
        <div className="mk-tagrow">
          {animal.type && <Pill tone="brand">{animal.type}</Pill>}
          {animal.category && <Pill tone="amber">{animal.category}</Pill>}
          {!hasTiers && animal.isFree && <Pill tone="brand">Free</Pill>}
          {!hasTiers && !animal.isFree && animal.onOffer && <Pill tone="amber">On offer</Pill>}
          {flatPreorder && <Pill tone="amber">Preorder</Pill>}
        </div>

        <h1 className="mk-page-title">{animal.name}</h1>

        <div className="mk-page-price-row">
          <Price
            value={hasTiers ? totalPrice : animal.price}
            size="lg"
            isFree={!hasTiers && animal.isFree}
            originalPrice={!hasTiers && animal.onOffer ? animal.originalPrice : 0}
          />
          <Mono className="mk-card-unit">{animal.stock || 0} in stock</Mono>
        </div>

        {isFlatStock && minQty > 1 && flatAvailable && (
          <p className="mk-note" style={{ marginTop: 0 }}>Minimum order: {minQty}.</p>
        )}
        {flatPreorder && (
          <p className="mk-note" style={{ marginTop: 0 }}>Currently out of stock — this can be ordered as a preorder and we'll confirm timing with you after checkout.</p>
        )}

        <Description text={animal.description} name={animal.name} />

        {animal.specifications && Object.keys(animal.specifications).length > 0 && (
          <div className="mk-specs">
            {Object.entries(animal.specifications).map(([k, v]) => (
              <div key={k} className="mk-spec">
                <Eyebrow>{k}</Eyebrow>
                <span>{v}</span>
              </div>
            ))}
          </div>
        )}

        {hasTiers && (
          <div className="mk-options">
            <Eyebrow>choose an option</Eyebrow>
            <TierSelector tiers={animal.pricingTiers} quantities={tierQty} onChange={setTierQtyValue} minQty={minQty} />
          </div>
        )}

        <button className="mk-btn-primary mk-page-cta" disabled={ctaDisabled} onClick={handleAdd}>
          <ShoppingBag size={16} /> {ctaLabel}
        </button>

        <a href={whatsappInquiryHref(animal.name)} target="_blank" rel="noopener noreferrer" className="mk-inquire-link">
          <MessageCircle size={14} /> Ask a question about this on WhatsApp
        </a>
      </div>
      </div>
      </div>

      <StickyMobileCta
        price={hasTiers ? totalPrice : animal.price}
        isFree={!hasTiers && animal.isFree}
        label={ctaLabel}
        disabled={ctaDisabled}
        onClick={handleAdd}
      />
    </div>
  );
}

/* ── Service application form — full name, email, phone, short message.
     Saved to the `service_applications` collection in Firestore. ── */
function ApplyServiceModal({ service, onClose }) {
  const [form, setForm] = useState({ fullName: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | submitting | done | error
  const [errors, setErrors] = useState({});

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = 'Please enter your full name';
    if (!form.phone.trim()) e.phone = 'Please enter a phone number';
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'That email looks incomplete';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setStatus('submitting');
    try {
      await addDoc(collection(db, 'service_applications'), {
        serviceId: service.id ?? null,
        serviceName: service.name,
        fullName: form.fullName.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim(),
        message: form.message.trim() || null,
        status: 'new',
        createdAt: serverTimestamp(),
      });
      // Also place it in the Enquiries inbox so every request reaches one list.
      try {
        await submitLead({
          name: form.fullName, email: form.email, phone: form.phone, role: 'Farmer',
          message: form.message || '', consent: true,
          subject: `Service request: ${service.name}`, source: 'website-shop-service',
        });
      } catch (e) { /* the application above is already saved */ }
      setStatus('done');
    } catch (err) {
      console.error('Error submitting service application:', err.message);
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <CenterModal onClose={onClose} title="Application sent" maxWidth={420}>
        <div className="mk-done">
          <div className="mk-done-mark"><Check size={26} /></div>
          <h4 className="mk-heading">Thank you, {form.fullName.split(' ')[0]}</h4>
          <p>We've received your interest in <strong>{service.name}</strong>. Our team will reach out on {form.phone} shortly.</p>
          <button className="mk-btn-primary" onClick={onClose}>Done</button>
        </div>
      </CenterModal>
    );
  }

  return (
    <CenterModal
      onClose={onClose}
      title={`Apply — ${service.name}`}
      maxWidth={440}
      footer={
        <button className="mk-btn-primary" disabled={status === 'submitting'} onClick={handleSubmit}>
          {status === 'submitting' ? <Loader2 size={16} className="mk-spin" /> : <Send size={15} />}
          {status === 'submitting' ? 'Sending…' : 'Send application'}
        </button>
      }
    >
      <p className="mk-copy" style={{ marginTop: 0 }}>Share a few details and our team will get back to you about {service.name.toLowerCase()}.</p>

      <div className="mk-field">
        <label>Full name</label>
        <input value={form.fullName} onChange={(e) => setField('fullName', e.target.value)} placeholder="e.g. Jane Wanjiru" />
        {errors.fullName && <span className="mk-field-error">{errors.fullName}</span>}
      </div>
      <div className="mk-field">
        <label>Email <span className="mk-muted">(optional)</span></label>
        <input value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="you@example.com" />
        {errors.email && <span className="mk-field-error">{errors.email}</span>}
      </div>
      <div className="mk-field">
        <label>Phone number</label>
        <input value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="07xx xxx xxx" />
        {errors.phone && <span className="mk-field-error">{errors.phone}</span>}
      </div>
      <div className="mk-field">
        <label>Short message <span className="mk-muted">(optional)</span></label>
        <textarea rows={3} value={form.message} onChange={(e) => setField('message', e.target.value)} placeholder="Tell us a little about what you need…" />
      </div>
      {status === 'error' && <p className="mk-field-error" style={{ marginTop: 4 }}>Something went wrong sending that — please try again.</p>}
    </CenterModal>
  );
}

function ServicePage({ service, onBack, onAddToCart }) {
  const [showApply, setShowApply] = useState(false);
  const [tierQty, setTierQty] = useState({});
  const hasTiers = service.hasTieredPricing && service.pricingTiers?.length > 0;
  // A service with a price, a free flag, or pricing tiers goes straight to
  // cart + payment, same as products/livestock/events. Only a service with
  // no pricing set at all (e.g. "contact us for a quote") falls back to the
  // plain inquiry/application form, since there's nothing to charge yet.
  const hasPricing = service.isFree || service.price > 0 || hasTiers;

  const tierTotalItems = useMemo(() => Object.values(tierQty).reduce((s, n) => s + n, 0), [tierQty]);
  const totalPrice = useMemo(() => {
    if (!hasTiers) return service.price;
    return service.pricingTiers.reduce((sum, t) => sum + (tierQty[t.id] || 0) * t.price, 0);
  }, [tierQty, service, hasTiers]);

  const setTierQtyValue = (id, n) => setTierQty((prev) => {
    const copy = { ...prev };
    if (n <= 0) delete copy[id]; else copy[id] = n;
    return copy;
  });

  const handleAdd = () => {
    if (hasTiers) {
      if (tierTotalItems === 0) return;
      service.pricingTiers.forEach((t) => {
        const n = tierQty[t.id] || 0;
        if (n > 0) onAddToCart({ ...service, price: t.price, tierName: t.name, tierId: t.id }, 'service', n);
      });
    } else {
      onAddToCart(service, 'service', 1);
    }
  };

  const ctaDisabled = hasTiers && tierTotalItems === 0;
  const ctaLabel = hasTiers
    ? (tierTotalItems > 0 ? `Add ${tierTotalItems} to cart` : 'Select an option')
    : (service.isFree ? 'Book for free' : 'Add to cart');

  return (
    <div className="mk-page">
      <button className="mk-page-back" onClick={onBack}><ArrowLeft size={16} /> Back to services</button>

      <div className="mk-page-layout">
      <div className="mk-page-media"><Gallery images={service.images} /></div>
      <div className="mk-page-info">
      <div className="mk-page-body">
        <h1 className="mk-page-title">{service.name}</h1>

        {!hasTiers && (service.isFree || service.onOffer || service.price > 0) && (
          <div className="mk-page-price-row">
            <span className="mk-muted">Starting price</span>
            <Price value={service.price} isFree={service.isFree} originalPrice={service.onOffer ? service.originalPrice : 0} />
          </div>
        )}

        <p className="mk-copy">{service.description}</p>

        {hasTiers && (
          <div className="mk-options">
            <Eyebrow>pricing options</Eyebrow>
            <TierSelector tiers={service.pricingTiers} quantities={tierQty} onChange={setTierQtyValue} />
          </div>
        )}

        {service.features?.length > 0 && (
          <>
            <Eyebrow>key features</Eyebrow>
            <div className="mk-feature-list">
              {service.features.map((f, i) => (
                <div key={i} className="mk-feature"><Check size={13} /> {f}</div>
              ))}
            </div>
          </>
        )}

        {service.audience?.length > 0 && (
          <>
            <Eyebrow>who it's for</Eyebrow>
            <div className="mk-tagrow">
              {service.audience.map((a, i) => <Pill key={i} tone="brand">{a}</Pill>)}
            </div>
          </>
        )}

        {hasPricing ? (
          <button className="mk-btn-primary mk-page-cta" disabled={ctaDisabled} onClick={handleAdd}>
            <ShoppingBag size={16} /> {ctaLabel}
          </button>
        ) : (
          <>
            <button className="mk-btn-primary mk-page-cta" onClick={() => setShowApply(true)}>
              <Send size={15} /> Apply for this service
            </button>
            <p className="mk-next">No payment now. Send your request and our team replies with a quote.</p>
          </>
        )}

        <Eyebrow>or get in touch directly</Eyebrow>
        <div className="mk-contact-list">
          <a href={CONTACT.phoneHref} className="mk-contact"><Phone size={15} /><div><span className="mk-muted">Call us</span><strong>{CONTACT.phoneDisplay}</strong></div></a>
          <a href={CONTACT.emailHref} className="mk-contact"><Mail size={15} /><div><span className="mk-muted">Email</span><strong>{CONTACT.email}</strong></div></a>
          <a href={whatsappInquiryHref(service.name)} target="_blank" rel="noopener noreferrer" className="mk-contact"><MessageCircle size={15} /><div><span className="mk-muted">WhatsApp</span><strong>Chat with us</strong></div></a>
        </div>
      </div>
      </div>
      </div>

      {hasPricing && (
        <StickyMobileCta
          price={hasTiers ? totalPrice : service.price}
          isFree={!hasTiers && service.isFree}
          label={ctaLabel}
          disabled={ctaDisabled}
          onClick={handleAdd}
        />
      )}

      <AnimatePresence>
        {showApply && <ApplyServiceModal service={service} onClose={() => setShowApply(false)} />}
      </AnimatePresence>
    </div>
  );
}

/* Events are purchasable, so — unlike services — they carry a full
   add-to-cart / checkout flow (ticket tiers behave just like livestock
   pricing tiers). They additionally surface date, location, capacity, and
   respect status/registration-deadline to close ticket sales when
   appropriate. */
function EventPage({ event, onBack, onAddToCart }) {
  const [tierQty, setTierQty] = useState({});
  const hasTiers = event.hasTieredPricing && event.pricingTiers?.length > 0;
  const tierTotalItems = useMemo(() => Object.values(tierQty).reduce((s, n) => s + n, 0), [tierQty]);
  const totalPrice = useMemo(() => {
    if (!hasTiers) return event.price;
    return event.pricingTiers.reduce((sum, t) => sum + (tierQty[t.id] || 0) * t.price, 0);
  }, [tierQty, event, hasTiers]);

  const setTierQtyValue = (id, n) => setTierQty((prev) => {
    const copy = { ...prev };
    if (n <= 0) delete copy[id]; else copy[id] = n;
    return copy;
  });

  const isClosed = event.status === 'past' || event.status === 'cancelled';
  const deadlinePassed = event.registrationDeadline ? new Date(event.registrationDeadline) < new Date() : false;
  const disabledReason = isClosed
    ? (event.status === 'cancelled' ? 'This event has been cancelled.' : 'This event has already taken place.')
    : deadlinePassed
      ? 'Registration for this event has closed.'
      : null;

  const handleAdd = () => {
    if (disabledReason) return;
    if (hasTiers) {
      if (tierTotalItems === 0) return;
      event.pricingTiers.forEach((t) => {
        const n = tierQty[t.id] || 0;
        if (n > 0) onAddToCart({ ...event, price: t.price, tierName: t.name, tierId: t.id }, 'event', n);
      });
    } else {
      onAddToCart(event, 'event', 1);
    }
  };

  const ctaDisabled = !!disabledReason || (hasTiers && tierTotalItems === 0);
  const ctaLabel = hasTiers
    ? (tierTotalItems > 0 ? `Get ${tierTotalItems} ticket${tierTotalItems > 1 ? 's' : ''}` : 'Select a ticket type')
    : (event.isFree ? 'Reserve a free spot' : 'Buy ticket');

  const dateRange = () => {
    if (!event.eventDate) return null;
    const opts = { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' };
    const startStr = new Date(event.eventDate).toLocaleString('en-KE', opts);
    if (!event.endDate) return startStr;
    const endStr = new Date(event.endDate).toLocaleString('en-KE', opts);
    return `${startStr} — ${endStr}`;
  };

  return (
    <div className="mk-page">
      <button className="mk-page-back" onClick={onBack}><ArrowLeft size={16} /> Back to events</button>

      <div className="mk-page-layout">
      <div className="mk-page-media"><Gallery images={event.images} /></div>
      <div className="mk-page-info">
      <div className="mk-page-body">
        <div className="mk-tagrow">
          {event.category && <Pill tone="amber">{event.category}</Pill>}
          {!hasTiers && event.isFree && <Pill tone="brand">Free</Pill>}
          {!hasTiers && !event.isFree && event.onOffer && <Pill tone="amber">On offer</Pill>}
          {isClosed && <Pill tone="amber">{event.status === 'cancelled' ? 'Cancelled' : 'Past event'}</Pill>}
        </div>

        <h1 className="mk-page-title">{event.name}</h1>

        <div className="mk-event-meta">
          {dateRange() && <div className="mk-event-meta-row"><Calendar size={15} /> {dateRange()}</div>}
          {event.location && <div className="mk-event-meta-row"><MapPin size={15} /> {event.location}</div>}
          {event.capacity !== '' && event.capacity != null && <div className="mk-event-meta-row"><Ticket size={15} /> {event.capacity} total slots</div>}
        </div>

        {!hasTiers && (
          <div className="mk-page-price-row">
            <Price value={event.price} size="lg" isFree={event.isFree} originalPrice={event.onOffer ? event.originalPrice : 0} />
          </div>
        )}

        <p className="mk-copy">{event.description}</p>

        {hasTiers && (
          <div className="mk-options">
            <Eyebrow>ticket type</Eyebrow>
            <TierSelector tiers={event.pricingTiers} quantities={tierQty} onChange={setTierQtyValue} />
          </div>
        )}

        {disabledReason ? (
          <p className="mk-note mk-note-warn">{disabledReason}</p>
        ) : (
          <button className="mk-btn-primary mk-page-cta" disabled={ctaDisabled} onClick={handleAdd}>
            <ShoppingBag size={16} /> {ctaLabel}
          </button>
        )}

        <a href={whatsappInquiryHref(event.name)} target="_blank" rel="noopener noreferrer" className="mk-inquire-link">
          <MessageCircle size={14} /> Ask a question about this on WhatsApp
        </a>
      </div>
      </div>
      </div>

      {!disabledReason && (
        <StickyMobileCta price={totalPrice} isFree={!hasTiers && event.isFree} label={ctaLabel} disabled={ctaDisabled} onClick={handleAdd} />
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   CART
   ════════════════════════════════════════════════════════════════════════ */
function CartModal({ cart, updateQuantity, removeFromCart, onClose, totalPrice, onCheckout }) {
  const itemKey = (item) => `${item.itemType}-${item.id}-${item.optionId || ''}-${item.tierId || ''}`;
  const displayName = (item) => {
    const parts = [item.name];
    if (item.animalType) parts.push(`— ${item.animalType}`);
    if (item.tierName) parts.push(`(${item.tierName})`);
    return parts.join(' ');
  };

  return (
    <CenterModal
      onClose={onClose}
      title="Your cart"
      maxWidth={460}
      footer={cart.length > 0 && (
        <>
          <div className="mk-line-row mk-total-row" style={{ borderTop: 'none', paddingTop: 0 }}>
            <span className="mk-muted">Total</span>
            <Price value={totalPrice} size="lg" />
          </div>
          <button className="mk-btn-primary" onClick={onCheckout}>Proceed to checkout <ArrowRight size={15} /></button>
        </>
      )}
    >
      {cart.length === 0 ? (
        <EmptyState icon={ShoppingBag} title="Your cart is empty" subtitle="Browse products, livestock, services or events to get started." />
      ) : (
        cart.map((item, idx) => {
          // Only flat-stock products/livestock (no option/tier — those
          // carry their own quantity rules) enforce a minimum order qty
          // on the cart's own stepper.
          const minQty = !item.optionId && !item.tierId ? getMinOrderQty(item) : 1;
          return (
          <div key={itemKey(item)} className={`mk-receipt-row ${idx === 0 ? 'first' : ''}`}>
            <SmartImage src={item.images?.[0]} width={120} fit="cover" alt={item.name} className="mk-receipt-img" />
            <div className="mk-receipt-info">
              <div className="mk-receipt-top">
                <span className="mk-receipt-name">
                  {displayName(item)}
                  {item.isPreorder && <span className="mk-cart-preorder-tag">Preorder</span>}
                </span>
                <IconButton icon={X} onClick={() => removeFromCart(item.id, item.itemType, item.optionId, item.tierId)} label="Remove item" subtle size={26} />
              </div>
              <span className="mk-card-unit">{item.unit || ''}{minQty > 1 ? ` · min ${minQty}` : ''}</span>
              <div className="mk-receipt-bottom">
                <Stepper
                  value={item.quantity}
                  onDecrement={() => updateQuantity(item.id, item.itemType, -1, item.optionId, item.tierId)}
                  onIncrement={() => updateQuantity(item.id, item.itemType, 1, item.optionId, item.tierId)}
                  disabledDecrement={item.quantity <= minQty}
                />
                <Price value={item.price * item.quantity} />
              </div>
            </div>
          </div>
          );
        })
      )}
    </CenterModal>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   CHECKOUT — collects the customer's details, then redirects them to
   Paystack's own hosted checkout page to pick and complete payment
   (M-Pesa, card, bank — whatever's enabled on the account). No phone
   number or payment method is collected here; that's entirely Paystack's
   screen. `initializePayment` creates the pending order server-side
   (recomputing prices from Firestore) and returns the checkout URL to
   redirect to. Paystack sends the customer back to this same page
   afterward with `?reference=...` in the URL, which `Shop` picks up on
   mount to confirm via `verifyPayment`. The `paystackWebhook` function
   remains the real source of truth and will confirm the order even if
   the customer closes the tab before making it back.
   ════════════════════════════════════════════════════════════════════════ */

// ── customer-detail input rules ──────────────────────────────────────────
// Full name: letters, spaces, apostrophes, hyphens and periods only — no
// digits. Sanitized live on every keystroke rather than only at submit, so
// a stray digit never sits in the field waiting to trip validation later.
const sanitizeFullName = (v) => v.replace(/[0-9]/g, '');
const FULL_NAME_PATTERN = /^[A-Za-z][A-Za-z\s'.-]*$/;

// Phone: digits only, with an optional leading "+" for international
// numbers. Anything else typed (letters, symbols) is stripped immediately.
const sanitizePhone = (v) => {
  const hasPlus = v.trim().startsWith('+');
  const digits = v.replace(/[^0-9]/g, '');
  return hasPlus ? `+${digits}` : digits;
};
const PHONE_PATTERN = /^\+?\d{9,13}$/;

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

const isDigitalItem = (i) => i.itemType === 'event' || /mbuziplus course/i.test(i.name || '');

function CheckoutModal({ cart, totalPrice, onClose, onBack }) {
  const digitalOnly = cart.length > 0 && cart.every(isDigitalItem);
  const [customer, setCustomer] = useState({
    fullName: '', phone: '', email: '', deliveryMethod: digitalOnly ? 'pickup' : 'delivery', address: '', agreedToDeliveryTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [redirecting, setRedirecting] = useState(false);
  const [payError, setPayError] = useState(null);

  const setField = (k, v) => setCustomer((c) => ({ ...c, [k]: v }));

  const validateDetails = () => {
    const e = {};
    const name = customer.fullName.trim();
    if (!name) e.fullName = 'Please enter your full name';
    else if (!FULL_NAME_PATTERN.test(name)) e.fullName = 'Full name can only contain letters — no numbers';

    const phone = customer.phone.trim();
    if (!phone) e.phone = 'Please enter a phone number';
    else if (!PHONE_PATTERN.test(phone)) e.phone = 'Enter a valid phone number, digits only (e.g. 07xx xxx xxx)';

    if (customer.email.trim() && !EMAIL_PATTERN.test(customer.email.trim())) e.email = 'That email looks incomplete';

    if (customer.deliveryMethod === 'delivery') {
      if (!customer.address.trim()) e.address = 'Please add a delivery address';
      if (!customer.agreedToDeliveryTerms) e.deliveryTerms = 'Please confirm you understand the delivery fee before continuing';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Creates the pending order server-side, then does a full-page redirect
  // to Paystack's hosted checkout — the customer picks their payment
  // method there (M-Pesa, card, or bank). No phone number or payment
  // method is ever sent from this client; `customer.phone` below is only
  // contact info for delivery/order updates, stored on the order, never
  // passed to Paystack. `callbackUrl` is this same page, so Paystack sends
  // them right back here (with ?reference=...) once they're done.
  const goToPay = async () => {
    if (!validateDetails()) return;
    setPayError(null);
    setRedirecting(true);
    try {
      const items = cart.map((i) => ({
        id: i.id, itemType: i.itemType, optionId: i.optionId || null, tierId: i.tierId || null, quantity: i.quantity,
      }));
      const callbackUrl = `${window.location.origin}${window.location.pathname}`;
      try { sessionStorage.setItem('mk-digital-order', digitalOnly ? '1' : ''); } catch (e) { /* ignore */ }
      const { data } = await callInitializePayment({ items, customer, callbackUrl });
      window.location.href = data.authorizationUrl;
    } catch (err) {
      console.error('initializePayment failed:', err.message);
      setRedirecting(false);
      setPayError(err.message || 'Could not start payment. Please try again.');
    }
  };

  return (
    <CenterModal
      onClose={onClose}
      title="Checkout"
      maxWidth={430}
      footer={
        <div className="mk-footer-stack">
          <button className="mk-btn-primary" disabled={redirecting} onClick={goToPay}>
            {redirecting ? <Loader2 size={16} className="mk-spin" /> : <>Continue to payment <ArrowRight size={15} /></>}
          </button>
          {!digitalOnly && <button className="mk-btn-secondary" disabled={redirecting} onClick={onBack}>Back to cart</button>}
        </div>
      }
    >
      <div className="mk-total-hero" style={{ paddingBottom: 8 }}>
        <span className="mk-muted">Total to pay</span>
        <Price value={totalPrice} size="lg" />
        <span className="mk-total-hero-count">{cart.reduce((s, i) => s + i.quantity, 0)} item(s)</span>
      </div>
      <Eyebrow>your details</Eyebrow>
      <div className="mk-field">
        <label>Full name</label>
        <input
          value={customer.fullName}
          onChange={(e) => setField('fullName', sanitizeFullName(e.target.value))}
          placeholder="e.g. Jane Wanjiru"
          inputMode="text"
          autoComplete="name"
        />
        {errors.fullName && <span className="mk-field-error">{errors.fullName}</span>}
      </div>
      <div className="mk-field">
        <label>Phone number <span className="mk-muted">{digitalOnly ? "(WhatsApp, for your access key)" : "(for delivery/order updates only)"}</span></label>
        <input
          value={customer.phone}
          onChange={(e) => setField('phone', sanitizePhone(e.target.value))}
          placeholder="07xx xxx xxx"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
        />
        {errors.phone && <span className="mk-field-error">{errors.phone}</span>}
      </div>
      <div className="mk-field">
        <label>Email <span className="mk-muted">(optional)</span></label>
        <input
          value={customer.email}
          onChange={(e) => setField('email', e.target.value)}
          placeholder="you@example.com"
          type="email"
          inputMode="email"
          autoComplete="email"
        />
        {errors.email
          ? <span className="mk-field-error">{errors.email}</span>
          : <span className="mk-field-hint">Add your email so we can send you an order receipt.</span>}
      </div>

      {digitalOnly ? (
        <p className="mk-note" style={{ marginTop: 12 }}>Nothing is delivered. After payment, our team sends your access key and the training dates on WhatsApp within 24 hours.</p>
      ) : (<>
      <Eyebrow>fulfillment</Eyebrow>
      <div className="mk-fulfillment-toggle">
        <button
          className={`mk-fulfillment-opt ${customer.deliveryMethod === 'delivery' ? 'is-active' : ''}`}
          onClick={() => setField('deliveryMethod', 'delivery')}
        >
          <MapPin size={14} /> Delivery
        </button>
        <button
          className={`mk-fulfillment-opt ${customer.deliveryMethod === 'pickup' ? 'is-active' : ''}`}
          onClick={() => setField('deliveryMethod', 'pickup')}
        >
          <Package size={14} /> Pickup
        </button>
      </div>
      {customer.deliveryMethod === 'delivery' && (
        <>
          <div className="mk-field">
            <label>Delivery address</label>
            <textarea rows={2} value={customer.address} onChange={(e) => setField('address', e.target.value)} placeholder="Estate / town, landmark, county" />
            {errors.address && <span className="mk-field-error">{errors.address}</span>}
          </div>

          <div className="mk-delivery-disclaimer">
            <p>
              A delivery fee will be charged in addition to the item total, based on your location. We'll confirm the exact
              fee with you before your order is dispatched.
            </p>
            <label className="mk-checkbox-row">
              <input
                type="checkbox"
                checked={customer.agreedToDeliveryTerms}
                onChange={(e) => setField('agreedToDeliveryTerms', e.target.checked)}
              />
              <span>I understand a delivery fee applies based on my location and will be confirmed before dispatch.</span>
            </label>
            {errors.deliveryTerms && <span className="mk-field-error">{errors.deliveryTerms}</span>}
          </div>
        </>
      )}
      {customer.deliveryMethod === 'pickup' && (
        <p className="mk-note" style={{ marginTop: 8 }}>We'll confirm a pickup location and time with you on {customer.phone || 'the number above'}.</p>
      )}
      </>)}

      {payError && <p className="mk-field-error" style={{ marginTop: 10 }}>{payError}</p>}

      <p className="mk-note" style={{ marginTop: 14 }}>
        You'll be taken to Paystack's secure checkout to choose how you'd like to pay — M-Pesa, card, or bank — then brought back here once it's done. We never see or store your payment details; that happens entirely on Paystack's page.
      </p>
    </CenterModal>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   ORDER RETURN — shown after Paystack redirects the customer back with
   ?reference=... in the URL. `status` walks: 'checking' (verifying with
   the server) → 'confirmed' | 'failed'.
   ════════════════════════════════════════════════════════════════════════ */
function ReturnStatusModal({ status, order, reference, onClose }) {
  if (status === 'checking') {
    return (
      <CenterModal onClose={() => {}} title="Confirming payment" maxWidth={380}>
        <div className="mk-done">
          <Loader2 size={28} className="mk-spin" style={{ color: 'var(--brand)', marginBottom: 10 }} />
          <p className="mk-copy" style={{ marginTop: 0 }}>Just a moment while we confirm your payment with Paystack…</p>
        </div>
      </CenterModal>
    );
  }

  if (status === 'confirmed') {
    return (
      <CenterModal onClose={onClose} title="Order placed" maxWidth={420}>
        <div className="mk-done">
          <div className="mk-done-mark"><Check size={26} /></div>
          <h4 className="mk-heading">Thank you{order?.customer?.fullName ? `, ${order.customer.fullName.split(' ')[0]}` : ''}</h4>
          <div className="mk-order-ref">
            <span className="mk-muted">Order reference</span>
            <Mono className="mk-order-ref-value">{order?.orderNumber || reference}</Mono>
          </div>
          <p>
            Your payment has been received{(() => { try { return sessionStorage.getItem('mk-digital-order') === '1'; } catch (e) { return false; } })() ? '. Our team will send your access key and the training dates on WhatsApp within 24 hours' : order?.fulfillment?.method === 'delivery' ? ', and your order will be delivered to the address you provided' : ', and it is ready for pickup'}.
          </p>
          <p>
            We'll reach out to you within the next 12–24 hours via WhatsApp or a phone call from our number, <strong>{CONTACT.phoneDisplay}</strong>, to confirm the details.
          </p>
          <button className="mk-btn-primary" onClick={onClose}>Done</button>
        </div>
      </CenterModal>
    );
  }

  // failed
  return (
    <CenterModal onClose={onClose} title="Payment not confirmed" maxWidth={420}>
      <div className="mk-done">
        <p className="mk-copy" style={{ marginTop: 0 }}>
          We couldn't confirm this payment{reference ? ` (ref: ${reference})` : ''}. If you completed checkout on Paystack, please contact us on {CONTACT.phoneDisplay} and we'll verify it manually. Otherwise, feel free to try again.
        </p>
        <button className="mk-btn-primary" onClick={onClose}>Close</button>
      </div>
    </CenterModal>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   FLOATING CART — always hovers bottom-left over the item grid, both on
   desktop and mobile, so it's the single place to reach the cart from.
   ════════════════════════════════════════════════════════════════════════ */
function FloatingCart({ totalItems, totalPrice, onOpen, hidden }) {
  return (
    <AnimatePresence>
      {!hidden && (
        <motion.button
          initial={{ scale: 0.85, opacity: 0, y: 12 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.85, opacity: 0, y: 12 }}
          transition={{ type: 'spring', damping: 26, stiffness: 340 }}
          onClick={onOpen}
          className="mk-fab"
          aria-label={totalItems > 0 ? `Open cart, ${totalItems} items, total KSh ${fmt(totalPrice)}` : 'Open cart'}
        >
          <span className="mk-fab-icon">
            <ShoppingBag size={17} />
            {totalItems > 0 && <span className="mk-fab-count">{totalItems}</span>}
          </span>
          <span className="mk-fab-total">{totalItems > 0 ? `KSh ${fmt(totalPrice)}` : 'Cart'}</span>
        </motion.button>
      )}
    </AnimatePresence>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   MAIN
   ════════════════════════════════════════════════════════════════════════ */
const MAIN_TABS = [
  { id: 'products', label: 'Products', icon: Package },
  { id: 'livestock', label: 'Livestock', icon: PawPrint },
  { id: 'services', label: 'Services', icon: Wrench },
  { id: 'events', label: 'Events', icon: Calendar },
];

// Normalizes the shared pricing-tier fields onto any fetched item so every
// downstream component can rely on them existing, regardless of whether
// the underlying Firestore doc was created before tiered pricing existed.
const withPricingDefaults = (data) => ({
  hasTieredPricing: !!data.hasTieredPricing,
  pricingTiers: data.pricingTiers || [],
  isFree: !!data.isFree,
  onOffer: !!data.onOffer,
  originalPrice: data.originalPrice || 0,
});

// Normalizes the shared inventory-rule fields (minimum order quantity +
// preorder-when-out-of-stock) onto any fetched product/livestock doc, so
// every downstream component can rely on them existing regardless of
// whether the underlying Firestore doc predates these fields.
const withInventoryDefaults = (data) => ({
  minOrderQuantity: Math.max(1, Number(data.minOrderQuantity) || 1),
  allowPreorder: !!data.allowPreorder,
});

/* ════════════════════════════════════════════════════════════════════════
   URL <-> ITEM MAPPING — `itemType` (singular: product/livestock/service/
   event) is what the cart and add-to-cart flow already use internally.
   The URL segment (plural: products/livestock/services/events) is what
   the Firestore subcollections under shop/categories/* are named, and
   what the `shopurlslug` Cloud Function's ALLOWED_TYPES checks against.
   Note `livestock` is the same word both ways — everything else differs.
   ════════════════════════════════════════════════════════════════════════ */
const TYPE_TO_URL_SEGMENT = { product: 'products', livestock: 'livestock', service: 'services', event: 'events' };
const URL_SEGMENT_TO_TYPE = { products: 'product', livestock: 'livestock', services: 'service', events: 'event' };

// Fallback slug generator — used until every Firestore doc has a
// ShopManager-authored `slug` field (see the deferred slug-generation
// note in shopurlslug.js). Pure slugify(name) — no ID suffix, for clean
// URLs like /shop/products/katya-goat-package.
//
// TRADEOFF: this means two items with the same name in the same category
// (e.g. two products both called "Goat Feed") slugify to the same string
// and become ambiguous — whichever one Firestore returns first wins, both
// here and in shopurlslug.js's matching lookup. Safe as long as names are
// effectively unique per category; the permanent fix is ShopManager
// writing a real, deduplicated `slug` field, which both this client and
// shopurlslug.js already prefer over the computed fallback the moment
// it's present (see `item.slug` check below / the `slug` field tier in
// shopurlslug.js's findItemBySlug).
const slugify = (str = '') => str
  .toString()
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '');

const slugFor = (item) => (item.slug ? item.slug : slugify(item.name));

// Builds the canonical /shop/:type/:slug URL for an item — this is the
// same shape shopurlslug's Hosting rewrite (/shop/:type/:slug) expects.
const itemUrl = (itemType, item) => `/shop/${TYPE_TO_URL_SEGMENT[itemType]}/${slugFor(item)}`;

// Parses a pathname back into { itemType, slug }, or null if it isn't a
// /shop/:type/:slug deep link (or the :type segment isn't recognized).
const parseShopItemPath = (pathname) => {
  const match = pathname.match(/^\/shop\/([^/]+)\/([^/]+)\/?$/);
  if (!match) return null;
  const itemType = URL_SEGMENT_TO_TYPE[match[1]];
  if (!itemType) return null;
  return { itemType, slug: decodeURIComponent(match[2]) };
};

const Shop = ({ isDark, onBack }) => {
  const [activeTab, setActiveTab] = useState('products');
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [productFilter, setProductFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [livestockFilter, setLivestockFilter] = useState('all');
  const [eventFilter, setEventFilter] = useState('all');
  const [sortBy, setSortBy] = useState('default');

  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedLivestock, setSelectedLivestock] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [toast, setToast] = useState(null);
  const [nearFooter, setNearFooter] = useState(false);

  // Set when the page loads with ?reference=... in the URL — i.e. Paystack
  // just redirected the customer back here after their hosted checkout.
  const [returnStatus, setReturnStatus] = useState(null); // null | 'checking' | 'confirmed' | 'failed'
  const [returnOrder, setReturnOrder] = useState(null);
  const [returnReference, setReturnReference] = useState(null);
  const verifiedRef = useRef(false);

  const [products, setProducts] = useState([]);
  const [livestock, setLivestock] = useState([]);
  const [services, setServices] = useState([]);
  const [events, setEvents] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingLivestock, setLoadingLivestock] = useState(true);
  const [loadingServices, setLoadingServices] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const fetchedProducts = useRef(false);
  const fetchedLivestock = useRef(false);
  const fetchedServices = useRef(false);
  const fetchedEvents = useRef(false);

  // Everything here is fetched from Firestore — there is no hardcoded
  // fallback data. Until a shop manager populates these collections,
  // people will simply see the polished empty states below.
  useEffect(() => {
    if (fetchedProducts.current) return;
    fetchedProducts.current = true;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'shop', 'categories', 'products'));
        setProducts(snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            price: data.price || data.basePrice || 0,
            basePrice: data.basePrice || data.price || 0,
            ...withPricingDefaults(data),
            ...withInventoryDefaults(data),
          };
        }));
      } catch (err) {
        console.error('Error fetching products:', err.message);
      } finally {
        setLoadingProducts(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (fetchedLivestock.current) return;
    fetchedLivestock.current = true;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'shop', 'categories', 'livestock'));
        setLivestock(snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            specifications: data.specifications || {},
            ...withPricingDefaults(data),
            ...withInventoryDefaults(data),
          };
        }));
      } catch (err) {
        console.error('Error fetching livestock:', err.message);
      } finally {
        setLoadingLivestock(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (fetchedServices.current) return;
    fetchedServices.current = true;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'shop', 'categories', 'services'));
        setServices(snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            features: data.features || [],
            audience: data.audience || [],
            // ShopManager saves service photos into `images[]` (same as products/
            // livestock) — there is no singular `image` field being written. Fall
            // back to it only for any legacy docs that still have it set.
            images: data.images?.length ? data.images : (data.image ? [data.image] : []),
            price: data.price || 0,
            ...withPricingDefaults(data),
          };
        }));
      } catch (err) {
        console.error('Error fetching services:', err.message);
      } finally {
        setLoadingServices(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (fetchedEvents.current) return;
    fetchedEvents.current = true;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'shop', 'categories', 'events'));
        setEvents(snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            price: data.price || 0,
            location: data.location || '',
            eventDate: data.eventDate || null,
            endDate: data.endDate || null,
            registrationDeadline: data.registrationDeadline || null,
            capacity: data.capacity ?? '',
            status: data.status || 'upcoming',
            ...withPricingDefaults(data),
          };
        }));
      } catch (err) {
        console.error('Error fetching events:', err.message);
      } finally {
        setLoadingEvents(false);
      }
    })();
  }, []);

  // ── Paystack return handler ──────────────────────────────────────────
  // Runs once on mount. If Paystack just redirected the customer back here
  // with ?reference=... in the URL, verify it server-side (`verifyPayment`)
  // and surface the result via `ReturnStatusModal`. The reference is
  // stripped from the URL right away so a page refresh doesn't re-trigger
  // verification or resurrect a stale status. `paystackWebhook` remains the
  // real source of truth on the backend — this is just how the customer
  // gets a confirmation screen without needing to leave this tab open.
  useEffect(() => {
    if (verifiedRef.current) return;
    verifiedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const reference = params.get('reference') || params.get('trxref');
    if (!reference) return;

    // Clean the URL immediately so refreshing/sharing this link doesn't
    // re-run verification against a reference that's already been handled.
    params.delete('reference');
    params.delete('trxref');
    const cleanedSearch = params.toString();
    const cleanedUrl = `${window.location.pathname}${cleanedSearch ? `?${cleanedSearch}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', cleanedUrl);

    setReturnReference(reference);
    setReturnStatus('checking');

    (async () => {
      try {
        const { data } = await callVerifyPayment({ reference });
        if (data.status === 'confirmed') {
          setReturnOrder(data.order || null);
          setReturnStatus('confirmed');
          // The order paid for is now settled — clear whatever's left in
          // the local cart so the customer doesn't accidentally pay twice.
          setCart([]);
        } else {
          setReturnStatus('failed');
        }
      } catch (err) {
        console.error('verifyPayment failed:', err.message);
        setReturnStatus('failed');
      }
    })();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      // Only treat the cart as "near the footer" when the page actually has
      // enough scrollable height for that to make sense — otherwise short
      // or still-loading pages would report a false positive and hide the
      // floating cart permanently.
      const scrollable = document.body.scrollHeight > window.innerHeight + 200;
      if (!scrollable) {
        setNearFooter(false);
        return;
      }
      const distanceFromBottom = document.body.scrollHeight - (window.scrollY + window.innerHeight);
      setNearFooter(distanceFromBottom < 480);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  };

  // Straight to checkout with only this item (used by the MbuziPlus Course: one tap from the page to payment).
  const buyNow = useCallback((item, itemType, quantity = 1) => {
    setCart([{ ...item, price: item.isFree ? 0 : item.price, itemType, quantity }]);
    setShowCart(false);
    setShowCheckout(true);
  }, []);

  const addToCart = useCallback((item, itemType, quantity = 1) => {
    // Options/tiers carry their own price (set explicitly by the calling
    // page), so only zero out the price for a top-level Free item, never
    // for a priced option or pricing tier.
    const effectivePrice = item.isFree && !item.optionId && !item.tierId ? 0 : item.price;
    setCart((prev) => {
      const match = (c) => c.id === item.id
        && c.itemType === itemType
        && (item.optionId ? c.optionId === item.optionId : !c.optionId)
        && (item.tierId ? c.tierId === item.tierId : !c.tierId);
      const existing = prev.find(match);
      if (existing) return prev.map((c) => (match(c) ? { ...c, quantity: c.quantity + quantity } : c));
      return [...prev, { ...item, price: effectivePrice, itemType, quantity }];
    });
    const label = item.tierName ? `${item.name} (${item.tierName})` : item.animalType ? `${item.name} (${item.animalType})` : item.name;
    showToast(`${label} added to cart`);
  }, []);

  const updateQuantity = useCallback((id, itemType, delta, optionId = null, tierId = null) => {
    setCart((prev) => prev
      .map((item) => {
        const match = item.id === id
          && item.itemType === itemType
          && (optionId ? item.optionId === optionId : !item.optionId)
          && (tierId ? item.tierId === tierId : !item.tierId);
        if (!match) return item;
        // Flat-stock cart lines (no option/tier) respect the item's own
        // minimum order quantity; option/tier lines floor at 1 as before.
        const floor = !item.optionId && !item.tierId ? getMinOrderQty(item) : 1;
        return { ...item, quantity: Math.max(floor, item.quantity + delta) };
      })
      .filter((item) => item.quantity > 0));
  }, []);

  const removeFromCart = useCallback((id, itemType, optionId = null, tierId = null) => {
    setCart((prev) => prev.filter((item) => !(
      item.id === id
      && item.itemType === itemType
      && (optionId ? item.optionId === optionId : !item.optionId)
      && (tierId ? item.tierId === tierId : !item.tierId)
    )));
  }, []);

  const totalItems = cart.reduce((s, i) => s + i.quantity, 0);
  const totalPrice = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  // Shared sort helper: works across every category via `cheapestPrice`
  // (so tiered items sort by their cheapest tier), and "newest" for any
  // collection carrying a createdAt-like field, falling back to insertion
  // order if none of that data exists.
  const applySort = useCallback((items) => {
    if (sortBy === 'default') return items;
    const sorted = [...items];
    if (sortBy === 'price-asc') sorted.sort((a, b) => cheapestPrice(a) - cheapestPrice(b));
    else if (sortBy === 'price-desc') sorted.sort((a, b) => cheapestPrice(b) - cheapestPrice(a));
    else if (sortBy === 'newest') {
      sorted.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });
    }
    return sorted;
  }, [sortBy]);

  const filteredProducts = useMemo(() => applySort(products
    .filter((p) => productFilter === 'all' || p.category === productFilter)
    .filter((p) => (p.name || '').toLowerCase().includes(search.toLowerCase()))), [products, productFilter, search, applySort]);

  const filteredLivestock = useMemo(() => applySort(livestock
    .filter((l) => livestockFilter === 'all' || l.category === livestockFilter)
    .filter((l) => (l.name || '').toLowerCase().includes(search.toLowerCase()))), [livestock, livestockFilter, search, applySort]);

  const filteredServices = useMemo(() => applySort(services
    .filter((s) => serviceFilter === 'all' || s.category === serviceFilter)
    .filter((s) => (s.name || '').toLowerCase().includes(search.toLowerCase()))), [services, serviceFilter, search, applySort]);

  const filteredEvents = useMemo(() => applySort(events
    .filter((e) => eventFilter === 'all' || e.category === eventFilter)
    .filter((e) => (e.name || '').toLowerCase().includes(search.toLowerCase()))), [events, eventFilter, search, applySort]);

  const productCategoryOptions = useMemo(() => buildCategoryOptions(products), [products]);
  const livestockCategoryOptions = useMemo(() => buildCategoryOptions(livestock, { ruminants: 'Goats & Sheep' }), [livestock]);
  const serviceCategoryOptions = useMemo(() => buildCategoryOptions(services), [services]);
  const eventCategoryOptions = useMemo(() => buildCategoryOptions(events), [events]);

  useEffect(() => { setSearch(''); setSearchOpen(false); }, [activeTab]);

  // Clears all four "selected item" states with no history side effect —
  // the shared primitive under closeDetail / selectItemSilently / openItem
  // below, so URL handling stays in exactly one place.
  const clearSelections = useCallback(() => {
    setSelectedProduct(null);
    setSelectedLivestock(null);
    setSelectedService(null);
    setSelectedEvent(null);
  }, []);

  // Sets whichever item page should be showing, without touching browser
  // history — used when the URL has already changed by the time we react
  // to it (initial deep-link resolution, and popstate/back-forward).
  const selectItemSilently = useCallback((itemType, item) => {
    clearSelections();
    const setters = {
      product: setSelectedProduct,
      livestock: setSelectedLivestock,
      service: setSelectedService,
      event: setSelectedEvent,
    };
    setters[itemType]?.(item);
  }, [clearSelections]);

  // Opens an item page from a click inside the app (a card, a quick-add
  // that needs type/tier selection first, etc). Unlike selectItemSilently,
  // this also pushes /shop/:type/:slug onto browser history so the address
  // bar, sharing, and shopurlslug's bot-preview rewrite all line up with
  // whatever's on screen.
  const openItem = useCallback((itemType, item) => {
    selectItemSilently(itemType, item);
    const url = itemUrl(itemType, item);
    if (window.location.pathname !== url) {
      window.history.pushState({ shopItem: { itemType, slug: slugFor(item) } }, '', url);
    }
  }, [selectItemSilently]);

  // Clears whichever item page is open, returning to the grid for the
  // active tab, and pops the URL back to /shop so Back/Forward and the
  // address bar stay in sync with what's on screen. Used by every page's
  // own "Back" control, and whenever the person switches tabs or types a
  // new search while a page is open.
  const closeDetail = useCallback(() => {
    clearSelections();
    const path = window.location.pathname;
    if (path !== '/shop' && path !== '/shop/') {
      window.history.pushState({}, '', '/shop');
    }
  }, [clearSelections]);

  const changeTab = (id) => { closeDetail(); setActiveTab(id); };

  // ── deep-link resolution — /shop/:type/:slug on first load ────────────
  // Reads the URL once on mount; once the matching category's data has
  // finished loading, looks the item up by slug and opens its page
  // directly (and switches the active tab to match) instead of leaving
  // the visitor on the default grid. Gives up silently if the slug isn't
  // found once loading completes (stale/broken link).
  const pendingDeepLinkRef = useRef(parseShopItemPath(window.location.pathname));
  useEffect(() => {
    const pending = pendingDeepLinkRef.current;
    if (!pending) return;
    const { itemType, slug } = pending;
    const collectionsByType = { product: products, livestock, service: services, event: events };
    const loadingByType = { product: loadingProducts, livestock: loadingLivestock, service: loadingServices, event: loadingEvents };
    if (loadingByType[itemType]) return; // wait for that category to finish loading
    const list = collectionsByType[itemType] || [];
    const match = list.find((it) => slugFor(it) === slug)
      || (slug === 'mbuziplus-course' ? list.find((it) => /mbuziplus course/i.test(it.name || '')) : null);
    if (match) {
      setActiveTab(TYPE_TO_URL_SEGMENT[itemType]); // tab ids are the same strings as URL segments
      selectItemSilently(itemType, match);
    }
    pendingDeepLinkRef.current = null; // resolved (found or not) — don't keep re-checking
  }, [products, livestock, services, events, loadingProducts, loadingLivestock, loadingServices, loadingEvents, selectItemSilently]);

  // ── back/forward while inside the shop ─────────────────────────────────
  // App.js's own popstate handler only reacts when the top-level *page*
  // changes; navigating between two /shop/... URLs (e.g. one item to
  // another, or an item back to the plain grid) never changes `currentPage`
  // away from 'shop', so App.js does nothing. Shop.jsx has to own its own
  // in-shop navigation instead.
  useEffect(() => {
    const onPopState = () => {
      const parsed = parseShopItemPath(window.location.pathname);
      if (parsed) {
        const collectionsByType = { product: products, livestock, service: services, event: events };
        const match = (collectionsByType[parsed.itemType] || []).find((it) => slugFor(it) === parsed.slug);
        if (match) {
          setActiveTab(TYPE_TO_URL_SEGMENT[parsed.itemType]);
          selectItemSilently(parsed.itemType, match);
          return;
        }
      }
      // Back to plain /shop, or an item that isn't loaded/found — fall
      // back to whatever grid is currently active.
      clearSelections();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [products, livestock, services, events, selectItemSilently, clearSelections]);

  const searchPlaceholder = activeTab === 'products' ? 'Search products…'
    : activeTab === 'livestock' ? 'Search livestock…'
      : activeTab === 'events' ? 'Search events…'
        : 'Search services…';

  const isDetailOpen = !!(selectedProduct || selectedLivestock || selectedService || selectedEvent);

  return (
    <div className={`imfuyo-market ${isDark ? 'dark' : ''}`}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700;800&family=Inter:wght@400;500;600;700&display=swap');

        .imfuyo-market {
          --canvas: #F7F8F4; --surface: #FFFFFF; --surface-alt: #F0F2EA;
          --ink: #10140F; --muted: #666D5D; --line: rgba(16,20,15,0.10);
          --brand: #285020; --brand-deep: #15803d; --brand-tint: #F1F5EE;
          --amber: #b45309; --amber-tint: #FFFBEB; --overlay: rgba(12,16,10,0.5);
          --shadow: rgba(16,20,15,0.10);
          font-family: 'Inter', ui-sans-serif, system-ui, sans-serif;
          background: var(--canvas); color: var(--ink); min-height: 100vh; padding-bottom: 64px;
          padding-top: 64px;
        }
        @media (min-width: 640px) { .imfuyo-market { padding-top: 80px; } }
        .imfuyo-market.dark {
          --canvas: #0F1410; --surface: #171D18; --surface-alt: #1E2620;
          --ink: #F2F4EF; --muted: #9BA396; --line: rgba(242,244,239,0.10);
          --brand: #3E7A32; --brand-deep: #8DBB7E; --brand-tint: #14301A;
          --amber: #DDB863; --amber-tint: #2C2716; --overlay: rgba(4,6,3,0.65);
          --shadow: rgba(0,0,0,0.4);
        }
        .imfuyo-market * { box-sizing: border-box; }
        .imfuyo-market .mk-heading { font-family: 'Sora', 'Inter', sans-serif; font-weight: 700; letter-spacing: -0.015em; margin: 0; font-size: 1.1rem; }
        .imfuyo-market .mk-mono { font-family: 'Sora', sans-serif; font-variant-numeric: tabular-nums; }
        .imfuyo-market .mk-price { font-family: 'Sora', sans-serif; font-weight: 700; color: var(--ink); font-variant-numeric: tabular-nums; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .imfuyo-market ::-webkit-scrollbar { width: 7px; }
        .imfuyo-market ::-webkit-scrollbar-thumb { background: var(--brand); border-radius: 5px; }

        /* ── focus states — keyboard users need a visible ring on every
           interactive element, not just the default browser outline which
           this file suppresses in a couple of places via custom controls. ── */
        .imfuyo-market button:focus-visible,
        .imfuyo-market a:focus-visible,
        .imfuyo-market input:focus-visible,
        .imfuyo-market textarea:focus-visible,
        .imfuyo-market select:focus-visible {
          outline: 2px solid var(--brand);
          outline-offset: 2px;
          border-radius: 6px;
        }

        /* ── images — every image in the marketplace fades in from the
           frame's neutral background instead of popping in, so a slow
           connection never shows a blank box or a broken-image flash. ── */
        .mk-smart-img { opacity: 0; transition: opacity .3s ease; display: block; }
        .mk-smart-img.is-loaded { opacity: 1; }

        /* ── sticky header ── */
        .mk-header { position: sticky; top: 64px; z-index: 40; background: color-mix(in srgb, var(--canvas) 90%, transparent); backdrop-filter: blur(12px); border-bottom: 1px solid var(--line); }
        @media (min-width: 640px) { .mk-header { top: 80px; } }
        .mk-header-inner { max-width: 1160px; margin: 0 auto; padding: 12px 16px 10px; }
        @media (min-width: 640px) { .mk-header-inner { padding: 16px 24px 12px; } }

        .mk-toprow { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
        .mk-back { border: none; background: none; color: var(--ink); padding: 6px; margin-left: -6px; border-radius: 999px; cursor: pointer; flex-shrink: 0; display: flex; }
        .mk-back:hover { background: var(--surface-alt); }
        .mk-wordmark { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 1rem; letter-spacing: -0.01em; white-space: nowrap; }
        .mk-wordmark small { display: block; font-family: 'Inter'; font-weight: 600; font-size: 0.58rem; letter-spacing: 0.14em; text-transform: uppercase; color: var(--muted); margin-top: -1px; }

        .mk-controlsrow { display: flex; align-items: center; gap: 8px; }
        .mk-segmented { display: flex; gap: 4px; background: var(--surface-alt); padding: 4px; border-radius: 12px; overflow-x: auto; flex: 1; min-width: 0; }
        .mk-segment { position: relative; display: flex; align-items: center; gap: 6px; flex-shrink: 0; background: none; border: none; padding: 8px 13px; border-radius: 9px; font-size: 0.82rem; font-weight: 600; color: var(--muted); cursor: pointer; white-space: nowrap; z-index: 1; }
        .mk-segment.is-active { color: #fff; }
        .mk-segment-bg { position: absolute; inset: 0; background: var(--brand); border-radius: 9px; z-index: -1; }
        .mk-segment-icon { flex-shrink: 0; }

        .mk-searchwrap { display: flex; align-items: center; flex-shrink: 0; }
        .mk-search-toggle { width: 36px; height: 36px; border-radius: 10px; border: 1px solid var(--line); background: var(--surface); color: var(--ink); display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
        .mk-search-toggle:hover { border-color: var(--brand); }
        .mk-search-field { position: relative; display: flex; align-items: center; overflow: hidden; margin-left: 8px; }
        .mk-search-field input { width: 100%; background: var(--surface); border: 1px solid var(--line); border-radius: 10px; padding: 8px 30px 8px 12px; font-size: 0.84rem; color: var(--ink); outline: none; }
        .mk-search-field input:focus { border-color: var(--brand); }
        .mk-search-field input::placeholder { color: var(--muted); }
        .mk-search-clear { position: absolute; right: 8px; background: none; border: none; color: var(--muted); cursor: pointer; display: flex; padding: 2px; }

        /* ── sort select ── */
        .mk-sortwrap { position: relative; flex-shrink: 0; display: flex; align-items: center; }
        .mk-sort { appearance: none; -webkit-appearance: none; background: var(--surface); border: 1px solid var(--line); color: var(--ink); font-size: 0.78rem; font-weight: 600; padding: 8px 26px 8px 11px; border-radius: 10px; cursor: pointer; font-family: 'Inter', sans-serif; height: 36px; }
        .mk-sort:hover { border-color: var(--brand); }
        .mk-sort-caret { position: absolute; right: 9px; pointer-events: none; color: var(--muted); }

        .mk-iconbtn { border: 1px solid var(--line); background: var(--surface); color: var(--ink); border-radius: 999px; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; transition: border-color .15s, transform .15s; }
        .mk-iconbtn:hover { border-color: var(--brand); }
        .mk-iconbtn.is-subtle { border: none; background: none; color: var(--muted); }
        .mk-iconbtn.is-subtle:hover { background: var(--surface-alt); color: var(--ink); }
        .mk-header-cart { position: relative; }
        .mk-header-cart-count { position: absolute; top: -3px; right: -3px; background: var(--brand); color: #fff; font-family: 'Sora'; font-size: 9px; font-weight: 700; min-width: 15px; height: 15px; border-radius: 999px; display: flex; align-items: center; justify-content: center; padding: 0 3px; border: 2px solid var(--canvas); }

        .mk-chiprow { display: flex; gap: 7px; overflow-x: auto; padding-top: 10px; }
        .mk-chip { flex-shrink: 0; border: 1px solid var(--line); background: var(--surface); color: var(--muted); font-size: 0.76rem; font-weight: 600; padding: 6px 13px; border-radius: 999px; cursor: pointer; white-space: nowrap; transition: all .15s; }
        .mk-chip.is-active { background: var(--ink); border-color: var(--ink); color: var(--canvas); }
        .mk-chip:not(.is-active):hover { border-color: var(--brand); color: var(--ink); }

        /* ── content ── */
        .mk-content { max-width: 1160px; margin: 0 auto; padding: 20px 16px 0; }
        @media (min-width: 640px) { .mk-content { padding: 24px 24px 0; } }

        /* ── item grids: fewer, larger columns so each card has more room ── */
        .mk-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px 14px; }
        @media (min-width: 640px) { .mk-grid { grid-template-columns: repeat(3, 1fr); gap: 24px 20px; } }
        .mk-service-grid { display: grid; grid-template-columns: 1fr; gap: 26px; }
        @media (min-width: 640px) { .mk-service-grid { grid-template-columns: repeat(2, 1fr); } }

        /* ── item cards: bordered container that lifts on hover. Images
           sit inside a softly inset frame with a consistent neutral
           background, shown in full via object-fit: contain — never
           cropped — so a tall bottle and a wide sack of feed both read
           clearly at a glance instead of one looking oddly zoomed. ── */
        .mk-item { background: var(--surface); border: 1px solid var(--line); border-radius: 18px; overflow: hidden; cursor: pointer; transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease; }
        .mk-item:hover { transform: translateY(-3px); box-shadow: 0 16px 30px -14px var(--shadow); border-color: var(--brand); }
        .mk-item.is-closed { opacity: 0.6; }
        .mk-item-img { position: relative; aspect-ratio: 1 / 1; overflow: hidden; background: var(--surface-alt); }
        .mk-item-img .mk-smart-img { width: 100%; height: 100%; transition: opacity .3s ease, transform .3s ease; }
        .mk-item:hover .mk-item-img .mk-smart-img { transform: scale(1.03); }
        .mk-item-img-contain { padding: 16px; }
        .mk-service-img { aspect-ratio: 16 / 10; padding: 0; }
        .mk-add-fab { position: absolute; right: 8px; bottom: 8px; width: 34px; height: 34px; border-radius: 999px; border: none; background: var(--ink); color: var(--canvas); display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 6px 14px -4px var(--shadow); transition: transform .15s, background .15s; }
        .mk-add-fab:hover { transform: scale(1.08); background: var(--brand); color: #fff; }
        .mk-add-fab:disabled { opacity: 0.4; cursor: not-allowed; transform: none; background: var(--ink); }
        .mk-item-body { padding: 13px 15px 16px; }
        @media (min-width: 640px) { .mk-item-body { padding: 17px 19px 19px; } }
        .mk-item-title { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 1rem; line-height: 1.3; margin: 0 0 8px; min-height: 2.6em; color: var(--ink); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        @media (min-width: 640px) { .mk-item-title { font-size: 1.08rem; } }
        .mk-item-row { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
        .mk-item-unit { font-size: 0.8rem; color: var(--muted); }
        .mk-item-priceline { display: flex; flex-wrap: wrap; align-items: baseline; gap: 2px 8px; margin-bottom: 12px; min-height: 2.2em; }
        .mk-item-priceline .mk-from { display: inline; margin: 0; font-size: 0.78rem; text-transform: none; letter-spacing: 0; font-weight: 500; }
        .mk-item-priceline .mk-price { font-size: 1.05rem !important; font-weight: 800; }
        .mk-card-btn { width: 100%; padding: 10px 12px; border-radius: 12px; border: 1.5px solid var(--brand); background: transparent; color: var(--brand-deep); font-weight: 700; font-size: 0.88rem; cursor: pointer; transition: background .15s, color .15s; }
        .mk-card-btn:hover:not(:disabled) { background: var(--brand); color: #fff; }
        .mk-card-btn:disabled { opacity: .5; cursor: not-allowed; border-color: var(--line); color: var(--muted); }
        .mk-how { background: var(--surface-alt); border: 1px solid var(--line); border-radius: 16px; padding: 14px 18px; margin-bottom: 20px; font-size: 0.92rem; line-height: 1.6; color: var(--ink); }
        .mk-how strong { display: block; margin-bottom: 4px; font-size: 1rem; }
        .mk-how ol { margin: 0 0 6px; padding-left: 20px; }
        .mk-how span { color: var(--muted); font-size: 0.86rem; }
        .mk-paybox { display: flex; flex-direction: column; gap: 3px; margin-top: 14px; padding: 12px 14px; border: 1px dashed var(--line); border-radius: 12px; font-size: 0.88rem; color: var(--ink); }
        .mk-next { margin: 12px 0 0; font-size: 0.86rem; line-height: 1.5; color: var(--muted); text-align: center; }
        .mk-item-desc { font-size: 0.85rem; color: var(--muted); line-height: 1.5; margin: 0 0 12px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .mk-card-link { font-size: 0.8rem; font-weight: 700; color: var(--brand-deep); display: inline-flex; align-items: center; gap: 3px; }
        .mk-card-unit { font-size: 0.66rem; color: var(--muted); white-space: nowrap; }
        .mk-from { display: block; font-size: 0.65rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--muted); margin-bottom: 2px; }
        .mk-event-card-meta { display: flex; flex-wrap: wrap; gap: 4px 12px; font-size: 0.74rem; font-weight: 600; color: var(--brand-deep); margin: 0 0 8px; }
        .mk-event-card-meta span { display: inline-flex; align-items: center; gap: 4px; }

        .mk-eyebrow { display: block; font-size: 0.64rem; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase; color: var(--muted); margin-bottom: 8px; margin-top: 16px; }

        .mk-pill { position: absolute; top: 8px; left: 8px; font-size: 0.62rem; font-weight: 700; letter-spacing: 0.02em; padding: 4px 9px; border-radius: 999px; color: #fff; }
        .mk-pill-brand { background: var(--brand); }
        .mk-pill-amber { background: var(--amber); }
        .mk-pill-right { left: auto; right: 8px; }

        .mk-price-wrap { display: inline-flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }
        .mk-price-free { color: var(--brand-deep); font-weight: 800; }
        .mk-price-original { font-family: 'Sora', sans-serif; font-size: 0.72em; font-weight: 600; color: var(--muted); text-decoration: line-through; }

        /* ── empty / skeleton ── */
        .mk-empty { grid-column: 1 / -1; display: flex; flex-direction: column; align-items: center; text-align: center; padding: 40px 20px; }
        .mk-empty-mark { width: 52px; height: 52px; border-radius: 999px; background: var(--surface-alt); color: var(--brand); display: flex; align-items: center; justify-content: center; margin-bottom: 14px; }
        .mk-empty-title { font-family: 'Sora'; font-weight: 700; margin: 0; }
        .mk-empty-sub { color: var(--muted); font-size: 0.85rem; margin: 4px 0 0; max-width: 260px; }
        .mk-skel-item { }
        .mk-skel-lines { padding-top: 9px; }
        .mk-skel-img { aspect-ratio: 1/1; background: var(--surface-alt); border-radius: 10px; animation: mk-pulse 1.4s ease-in-out infinite; }
        .mk-skel-line { height: 10px; border-radius: 4px; background: var(--surface-alt); animation: mk-pulse 1.4s ease-in-out infinite; }
        @keyframes mk-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }

        /* ── floating cart (bottom-left, hovers over the item grid) ── */
        .mk-fab { position: fixed; left: 16px; bottom: calc(16px + env(safe-area-inset-bottom, 0px)); z-index: 44; display: flex; align-items: center; gap: 10px; background: var(--ink); color: var(--canvas); border: none; border-radius: 999px; padding: 8px 16px 8px 8px; cursor: pointer; box-shadow: 0 14px 30px -10px var(--shadow); }
        .mk-fab-icon { position: relative; width: 34px; height: 34px; border-radius: 999px; background: var(--brand); color: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .mk-fab-count { position: absolute; top: -4px; right: -4px; background: var(--amber); color: #fff; font-family: 'Sora'; font-size: 9px; font-weight: 800; min-width: 16px; height: 16px; border-radius: 999px; display: flex; align-items: center; justify-content: center; padding: 0 3px; border: 2px solid var(--ink); }
        .mk-fab-total { font-family: 'Sora'; font-size: 0.84rem; font-weight: 700; white-space: nowrap; }

        /* ── toast ── */
        .mk-toast { position: fixed; left: 50%; bottom: 22px; z-index: 70; background: var(--ink); color: var(--canvas); padding: 11px 16px; border-radius: 12px; display: flex; align-items: center; gap: 9px; font-size: 0.83rem; font-weight: 500; box-shadow: 0 14px 30px -10px rgba(0,0,0,0.4); max-width: min(88vw, 360px); }
        .mk-toast-mark { width: 18px; height: 18px; border-radius: 999px; background: var(--brand); color: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }

        /* ── center modal (desktop) / bottom sheet (mobile) — cart, checkout,
           type picker and the service application form still use this. ── */
        .mk-overlay { position: fixed; inset: 0; background: var(--overlay); z-index: 60; display: flex; align-items: center; justify-content: center; padding: 20px; }
        .mk-modal { width: 100%; background: var(--surface); border-radius: 24px; max-height: 86vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 30px 70px -20px rgba(0,0,0,0.45); }
        .mk-modal-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 18px 20px 4px; flex-shrink: 0; }
        .mk-modal-body { padding: 14px 20px 6px; overflow-y: auto; }
        .mk-modal-footer { padding: 14px 20px 20px; border-top: 1px solid var(--line); flex-shrink: 0; display: flex; flex-direction: column; gap: 8px; }

        @media (max-width: 639px) {
          .mk-overlay { align-items: flex-end; padding: 0; }
        }
        .mk-modal-sheet {
          width: 100%;
          max-width: 100%;
          border-radius: 20px 20px 0 0;
          max-height: 92vh;
        }
        .mk-modal-sheet .mk-modal-footer { padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px)); }
        .mk-modal-sheet .mk-modal-header { padding-top: 8px; }
        .mk-sheet-handle {
          width: 38px;
          height: 4px;
          border-radius: 999px;
          background: var(--line);
          margin: 10px auto 0;
          flex-shrink: 0;
        }

        .mk-total-hero { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 4px; padding: 6px 0 18px; }
        .mk-total-hero-count { font-size: 0.72rem; color: var(--muted); }

        .mk-desc { margin: 18px 0 8px; }
        .mk-desc-lead { font-size: 1.02rem; line-height: 1.65; color: var(--ink, inherit); margin: 0 0 12px; font-weight: 500; }
        .mk-desc-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
        .mk-desc-list li { position: relative; padding-left: 22px; font-size: 0.92rem; line-height: 1.6; color: var(--muted); }
        .mk-desc-list li::before { content: ""; position: absolute; left: 4px; top: 0.55em; width: 7px; height: 7px; border-radius: 50%; background: #285020; }
        .mk-desc-sub { font-size: 0.72rem; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; color: #285020; margin: 18px 0 10px; }
        .mk-desc-steps { list-style: none; margin: 0; padding: 0; display: grid; gap: 12px; }
        .mk-desc-steps li { display: flex; gap: 12px; align-items: flex-start; font-size: 0.92rem; line-height: 1.55; color: var(--muted); }
        .mk-desc-steps strong { color: var(--ink, inherit); }
        .mk-desc-n { flex: 0 0 26px; height: 26px; border-radius: 50%; background: #285020; color: #fff; font-weight: 800; font-size: 0.8rem; display: inline-flex; align-items: center; justify-content: center; }
        .mk-desc-note { margin: 14px 0 0; font-size: 0.82rem; line-height: 1.5; color: var(--muted); }
        .mk-desc-link { color: #285020; font-weight: 600; text-decoration: underline; text-underline-offset: 2px; word-break: break-word; }
        .mk-copy { font-size: 0.87rem; line-height: 1.65; color: var(--muted); margin: 16px 0 0; }
        .mk-line-row { display: flex; align-items: center; justify-content: space-between; padding: 10px 0; border-top: 1px solid var(--line); font-size: 0.88rem; }
        .mk-total-row { font-size: 1rem; }
        .mk-muted { color: var(--muted); }
        .mk-btn-primary { width: 100%; background: var(--brand); color: #fff; border: none; padding: 14px; border-radius: 12px; font-weight: 700; font-size: 0.92rem; display: flex; align-items: center; justify-content: center; gap: 8px; cursor: pointer; transition: background .15s; font-family: 'Inter', sans-serif; }
        .mk-btn-primary:hover { background: var(--brand-deep); }
        .mk-btn-primary:disabled { background: var(--surface-alt); color: var(--muted); cursor: not-allowed; }
        .mk-btn-secondary { width: 100%; background: var(--surface-alt); color: var(--ink); border: none; padding: 13px; border-radius: 12px; font-weight: 700; font-size: 0.9rem; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; font-family: 'Inter', sans-serif; }
        .mk-footer-stack { display: flex; flex-direction: column; gap: 8px; }

        /* ── forms (checkout details + apply form) ── */
        .mk-field { margin-top: 14px; display: flex; flex-direction: column; gap: 5px; }
        .mk-field label { font-size: 0.78rem; font-weight: 700; color: var(--ink); }
        .mk-field input, .mk-field textarea { border: 1.5px solid var(--line); background: var(--surface); border-radius: 10px; padding: 10px 12px; font-size: 0.87rem; color: var(--ink); font-family: inherit; outline: none; resize: vertical; }
        .mk-field input:focus, .mk-field textarea:focus { border-color: var(--brand); }
        .mk-field-error { font-size: 0.72rem; color: #dc2626; font-weight: 600; }
        .mk-field-hint { font-size: 0.72rem; color: var(--brand-deep); font-weight: 500; }
        .mk-spin { animation: mk-spin 0.8s linear infinite; }
        @keyframes mk-spin { to { transform: rotate(360deg); } }

        /* ── delivery vs pickup toggle ── */
        .mk-fulfillment-toggle { display: flex; gap: 8px; margin-top: 6px; }
        .mk-fulfillment-opt { flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; border: 1.5px solid var(--line); background: var(--surface); color: var(--muted); font-weight: 700; font-size: 0.82rem; padding: 10px; border-radius: 10px; cursor: pointer; font-family: 'Inter', sans-serif; transition: border-color .15s, background .15s, color .15s; }
        .mk-fulfillment-opt.is-active { border-color: var(--brand); background: var(--brand-tint); color: var(--brand-deep); }

        /* ── delivery-fee disclaimer + acknowledgement checkbox ── */
        .mk-delivery-disclaimer { margin-top: 14px; border: 1.5px solid var(--amber); background: var(--amber-tint); border-radius: 12px; padding: 12px 14px; }
        .mk-delivery-disclaimer p { margin: 0 0 10px; font-size: 0.78rem; line-height: 1.5; color: var(--ink); }
        .mk-checkbox-row { display: flex; align-items: flex-start; gap: 8px; cursor: pointer; }
        .mk-checkbox-row input[type="checkbox"] { margin-top: 2px; width: 16px; height: 16px; flex-shrink: 0; accent-color: var(--amber); }
        .mk-checkbox-row span { font-size: 0.78rem; font-weight: 600; color: var(--ink); line-height: 1.45; }

        /* ── preorder tag inline with a cart line's name ── */
        .mk-cart-preorder-tag { display: inline-block; margin-left: 6px; font-size: 0.62rem; font-weight: 800; letter-spacing: 0.02em; text-transform: uppercase; color: var(--amber); background: var(--amber-tint); border-radius: 999px; padding: 2px 7px; vertical-align: middle; }

        /* ── order reference on the confirmation screen ── */
        .mk-order-ref { display: flex; flex-direction: column; align-items: center; gap: 2px; margin: 4px 0 10px; }
        .mk-order-ref-value { font-size: 0.95rem; font-weight: 700; letter-spacing: 0.02em; color: var(--brand-deep); }

        /* ── option summary + swatch grid (still a modal — quick sub-picker) ── */
        .mk-options { margin-top: 18px; }
        .mk-opt-summary { border: 1.5px dashed var(--line); border-radius: 14px; padding: 14px; }
        .mk-opt-summary-empty { color: var(--muted); font-size: 0.85rem; margin: 0 0 10px; }
        .mk-opt-chip-row { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
        .mk-opt-chip { display: inline-flex; align-items: center; gap: 6px; background: var(--brand-tint); color: var(--brand-deep); border-radius: 999px; padding: 5px 10px; font-size: 0.76rem; font-weight: 700; }
        .mk-opt-select-btn { width: 100%; border: 1.5px solid var(--brand); background: none; color: var(--brand-deep); font-weight: 700; font-size: 0.85rem; padding: 11px; border-radius: 12px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; font-family: 'Inter', sans-serif; }
        .mk-opt-select-btn:hover { background: var(--brand-tint); }

        .mk-swatch-grid { display: grid; grid-template-columns: 1fr; gap: 10px; }
        .mk-swatch { border: 1.5px solid var(--line); border-radius: 14px; overflow: hidden; transition: border-color .15s, background .15s; }
        .mk-swatch.is-selected { border-color: var(--brand); background: var(--brand-tint); }
        .mk-swatch.is-disabled { opacity: 0.5; }
        .mk-swatch-main { width: 100%; display: flex; align-items: center; gap: 11px; padding: 12px 13px 6px; background: none; border: none; cursor: pointer; text-align: left; font-family: inherit; }
        .mk-swatch-main:disabled { cursor: not-allowed; }
        .mk-swatch-icon { width: 32px; height: 32px; border-radius: 9px; background: var(--surface-alt); color: var(--brand-deep); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .mk-swatch.is-selected .mk-swatch-icon { background: var(--brand); color: #fff; }
        .mk-swatch-text { flex: 1; min-width: 0; }
        .mk-swatch-name { display: block; font-weight: 700; font-size: 0.87rem; color: var(--ink); }
        .mk-swatch-desc { display: block; font-size: 0.74rem; color: var(--muted); margin-top: 2px; line-height: 1.4; }
        .mk-swatch-stock { font-weight: 600; color: var(--amber); }
        .mk-swatch-check { width: 19px; height: 19px; border-radius: 999px; background: var(--brand); color: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .mk-swatch-foot { display: flex; align-items: center; gap: 8px; padding: 6px 13px 12px 56px; flex-wrap: wrap; }
        .mk-swatch-unit { font-size: 0.68rem; color: var(--muted); }
        .mk-swatch-stepper-wrap { margin-left: auto; }

        /* ── read-only pricing-tier list (services) ── */
        .mk-tier-list { display: flex; flex-direction: column; gap: 8px; margin-bottom: 4px; }
        .mk-tier-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; border: 1.5px solid var(--line); border-radius: 12px; padding: 11px 13px; }
        .mk-tier-text { display: flex; flex-direction: column; gap: 2px; }
        .mk-tier-name { font-weight: 700; font-size: 0.86rem; }
        .mk-tier-desc { font-size: 0.76rem; color: var(--muted); }

        .mk-stepper { display: flex; align-items: center; gap: 9px; flex-shrink: 0; }
        .mk-stepper button { width: 27px; height: 27px; border-radius: 8px; border: 1px solid var(--line); background: var(--surface); color: var(--ink); display: flex; align-items: center; justify-content: center; cursor: pointer; }
        .mk-stepper button:last-child { background: var(--brand); border-color: var(--brand); color: #fff; }
        .mk-stepper button:disabled { opacity: 0.35; cursor: not-allowed; }
        .mk-stepper-value { width: 18px; text-align: center; font-weight: 700; font-size: 0.85rem; }
        .mk-stepper.is-small button { width: 22px; height: 22px; border-radius: 7px; }
        .mk-stepper.is-small .mk-stepper-value { width: 14px; font-size: 0.76rem; }

        .mk-tagrow { display: flex; flex-wrap: wrap; gap: 7px; margin: 14px 0; position: static; }
        .mk-tagrow .mk-pill { position: static; color: #fff; }
        .mk-specs { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; background: var(--surface-alt); border-radius: 14px; padding: 14px; margin-top: 16px; }
        .mk-spec { display: flex; flex-direction: column; gap: 2px; font-size: 0.85rem; font-weight: 600; }

        /* ── event meta (date / location / capacity) on the event page ── */
        .mk-event-meta { display: flex; flex-direction: column; gap: 6px; margin: 4px 0 14px; }
        .mk-event-meta-row { display: flex; align-items: center; gap: 8px; font-size: 0.85rem; font-weight: 600; color: var(--ink); }
        .mk-event-meta-row svg { color: var(--brand); flex-shrink: 0; }
        .mk-note-warn { color: var(--amber); font-weight: 600; margin-top: 18px; }

        .mk-feature-list { display: flex; flex-direction: column; gap: 8px; margin: 4px 0 18px; }
        .mk-feature { display: flex; align-items: center; gap: 8px; font-size: 0.86rem; color: var(--ink); }
        .mk-feature svg { color: var(--brand); flex-shrink: 0; }
        .mk-contact-list { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; }
        .mk-contact { display: flex; align-items: center; gap: 12px; border: 1px solid var(--line); border-radius: 12px; padding: 11px 13px; color: var(--ink); text-decoration: none; transition: border-color .15s, background .15s; }
        .mk-contact:hover { border-color: var(--brand); background: var(--surface-alt); }
        .mk-contact svg { color: var(--brand); flex-shrink: 0; }
        .mk-contact div { display: flex; flex-direction: column; gap: 1px; }
        .mk-contact span.mk-muted { font-size: 0.68rem; }
        .mk-contact strong { font-size: 0.85rem; }

        /* ── receipt (cart popup) ── */
        .mk-receipt-row { display: flex; gap: 12px; padding: 14px 0; border-top: 1px dashed var(--line); }
        .mk-receipt-row.first { border-top: none; padding-top: 0; }
        .mk-receipt-row .mk-receipt-img { width: 58px; height: 58px; border-radius: 10px; flex-shrink: 0; background: var(--surface-alt); }
        .mk-receipt-info { flex: 1; min-width: 0; }
        .mk-receipt-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
        .mk-receipt-name { font-weight: 700; font-size: 0.87rem; }
        .mk-receipt-bottom { display: flex; align-items: center; justify-content: space-between; margin-top: 8px; }

        .mk-stub { background: var(--surface-alt); border-radius: 14px; padding: 16px; margin-bottom: 14px; }
        .mk-pay-logos { display: flex; gap: 10px; justify-content: center; margin-bottom: 14px; }
        .mk-pay-logos img { height: 30px; object-fit: contain; border-radius: 6px; }
        .mk-copyfield { margin-top: 12px; }
        .mk-copyfield-row { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
        .mk-copyfield-value { flex: 1; background: var(--surface); border: 1px solid var(--line); border-radius: 9px; padding: 9px 11px; font-weight: 700; font-size: 0.85rem; }
        .mk-note { font-size: 0.75rem; color: var(--muted); margin-top: 12px; line-height: 1.5; }
        .mk-disclosure { width: 100%; display: flex; align-items: center; justify-content: space-between; background: var(--surface-alt); border: none; border-radius: 12px; padding: 13px 15px; font-weight: 700; font-size: 0.85rem; color: var(--ink); cursor: pointer; margin-top: 4px; }
        .mk-terms { font-size: 0.76rem; color: var(--muted); line-height: 1.55; padding: 12px 4px 4px; display: flex; flex-direction: column; gap: 8px; }

        .mk-done { display: flex; flex-direction: column; align-items: center; text-align: center; padding: 10px 8px 4px; gap: 6px; }
        .mk-done-mark { width: 56px; height: 56px; border-radius: 999px; background: var(--brand); color: #fff; display: flex; align-items: center; justify-content: center; margin-bottom: 8px; }
        .mk-done h4 { font-size: 1.2rem; }
        .mk-done p { font-size: 0.85rem; color: var(--muted); line-height: 1.55; margin: 2px 0 16px; }
        .mk-done .mk-btn-primary { width: 100%; }

        /* ── gallery — the main frame keeps a fixed neutral background so
           switching photos, or waiting for one to load, never causes a
           flash or a jump; object-fit: contain keeps the whole subject
           visible regardless of its native aspect ratio. ── */
        .mk-gallery-frame { position: relative; aspect-ratio: 1/1; overflow: hidden; background: var(--surface-alt); }
        .mk-gallery-frame-inner { position: absolute; inset: 0; padding: 18px; }
        .mk-gallery-frame .mk-smart-img { width: 100%; height: 100%; }
        .mk-gallery-nav { position: absolute; top: 50%; transform: translateY(-50%); width: 34px; height: 34px; border-radius: 999px; border: none; background: color-mix(in srgb, var(--surface) 88%, transparent); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 4px 12px var(--shadow); z-index: 2; }
        .mk-gallery-nav.prev { left: 10px; } .mk-gallery-nav.next { right: 10px; }
        .mk-gallery-count { position: absolute; bottom: 10px; right: 10px; background: color-mix(in srgb, var(--ink) 75%, transparent); color: var(--canvas); font-size: 0.68rem; padding: 3px 8px; border-radius: 999px; z-index: 2; }
        .mk-gallery-rail { display: flex; gap: 8px; margin-top: 10px; overflow-x: auto; }
        .mk-thumb { flex-shrink: 0; width: 52px; height: 52px; border-radius: 10px; overflow: hidden; border: 2px solid transparent; padding: 0; cursor: pointer; background: var(--surface-alt); }
        .mk-thumb.is-active { border-color: var(--brand); }
        .mk-thumb .mk-smart-img { width: 100%; height: 100%; }

        /* ── full item page — replaces the old center-modal item popups.
           The hero image bleeds to the edge of the content column so it
           reads as part of the page, not a separate boxed section. ── */
        .mk-page { max-width: 1080px; margin: 0 auto; }
        .mk-page-back { display: flex; align-items: center; gap: 6px; background: none; border: none; color: var(--muted); font-weight: 600; font-size: 0.85rem; padding: 2px 0 16px; cursor: pointer; font-family: 'Inter', sans-serif; }
        .mk-page-back:hover { color: var(--brand-deep); }

        /* Image on the left, details on the right from tablet width up;
           stacked (image on top) on narrow screens. */
        .mk-page-layout { display: flex; flex-direction: column; gap: 20px; }
        @media (min-width: 860px) { .mk-page-layout { flex-direction: row; align-items: flex-start; gap: 44px; } }
        .mk-page-media { }
        @media (min-width: 860px) { .mk-page-media { flex: 0 0 46%; position: sticky; top: 96px; } }
        .mk-page .mk-gallery-frame { border-radius: 16px; background: var(--surface-alt); aspect-ratio: 4 / 3; }
        @media (min-width: 860px) { .mk-page .mk-gallery-frame { aspect-ratio: 1 / 1; } }
        .mk-page-info { flex: 1 1 auto; min-width: 0; }
        .mk-page-body { padding-top: 20px; padding-bottom: 90px; }
        @media (min-width: 860px) { .mk-page-body { padding-top: 0; padding-bottom: 0; } }
        .mk-page-title { font-family: 'Sora', sans-serif; font-weight: 800; font-size: 1.4rem; letter-spacing: -0.02em; margin: 2px 0 10px; }
        @media (min-width: 640px) { .mk-page-title { font-size: 1.7rem; } }
        .mk-page-price-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 4px; }
        .mk-page-cta { margin-top: 24px; }

        /* Direct WhatsApp inquiry link under the CTA on product/livestock/event pages. */
        .mk-inquire-link { display: flex; align-items: center; justify-content: center; gap: 7px; margin-top: 10px; font-size: 0.82rem; font-weight: 600; color: var(--brand-deep); text-decoration: none; padding: 8px; }
        .mk-inquire-link:hover { text-decoration: underline; }

        /* ── sticky mobile CTA bar — pinned above the safe area, mobile only ── */
        .mk-sticky-cta {
          display: none;
          position: fixed;
          left: 0; right: 0; bottom: 0;
          z-index: 42;
          align-items: center;
          gap: 12px;
          padding: 10px 16px calc(10px + env(safe-area-inset-bottom, 0px));
          background: color-mix(in srgb, var(--surface) 96%, transparent);
          backdrop-filter: blur(10px);
          border-top: 1px solid var(--line);
          box-shadow: 0 -8px 24px -12px var(--shadow);
        }
        .mk-sticky-cta-price { flex-shrink: 0; }
        .mk-sticky-cta-btn { width: auto; flex: 1; padding: 12px 16px; }
        @media (max-width: 859px) {
          .mk-sticky-cta { display: flex; }
          /* keep the floating cart from overlapping the sticky bar on item pages */
          .mk-page ~ .mk-fab { display: none; }
        }
      `}</style>

      {/* ══ sticky header ══ */}
      <div className="mk-header">
        <div className="mk-header-inner">
          <div className="mk-toprow">
            {onBack && <button onClick={onBack} className="mk-back" aria-label="Back"><ChevronLeft size={20} /></button>}
            <div className="mk-wordmark">Imfuyo<small>Marketplace</small></div>
            <div style={{ flex: 1 }} />
            <div className="mk-header-cart">
              <IconButton icon={ShoppingBag} onClick={() => setShowCart(true)} label="Open cart" size={36} />
              {totalItems > 0 && <span className="mk-header-cart-count">{totalItems}</span>}
            </div>
          </div>

          {!isDetailOpen && (
            <>
              <div className="mk-controlsrow">
                <SegmentedTabs tabs={MAIN_TABS} active={activeTab} onChange={changeTab} />
                <SortSelect value={sortBy} onChange={setSortBy} />
                <SearchExpand value={search} onChange={setSearch} placeholder={searchPlaceholder} open={searchOpen} setOpen={setSearchOpen} />
              </div>

              {activeTab === 'products' && <FilterChips options={productCategoryOptions} active={productFilter} onChange={setProductFilter} />}
              {activeTab === 'services' && <FilterChips options={serviceCategoryOptions} active={serviceFilter} onChange={setServiceFilter} />}
              {activeTab === 'livestock' && <FilterChips options={livestockCategoryOptions} active={livestockFilter} onChange={setLivestockFilter} />}
              {activeTab === 'events' && <FilterChips options={eventCategoryOptions} active={eventFilter} onChange={setEventFilter} />}
            </>
          )}
        </div>
      </div>

      {/* ══ content: either the grid for the active tab, or the full page
           for whichever item was opened ══ */}
      <div className="mk-content">
        {selectedProduct ? (
          <ProductPage product={selectedProduct} onBack={closeDetail} onAddToCart={addToCart} onBuyNow={buyNow} />
        ) : selectedLivestock ? (
          <LivestockPage animal={selectedLivestock} onBack={closeDetail} onAddToCart={addToCart} />
        ) : selectedService ? (
          <ServicePage service={selectedService} onBack={closeDetail} onAddToCart={addToCart} />
        ) : selectedEvent ? (
          <EventPage event={selectedEvent} onBack={closeDetail} onAddToCart={addToCart} />
        ) : (
          <>
            {activeTab === 'products' && !loadingProducts && filteredProducts.length > 0 && (
              <div className="mk-how">
                <strong>How buying works</strong>
                <ol>
                  <li>Tap a product and choose what you want.</li>
                  <li>Add it to your cart and check out.</li>
                  <li>Pay on the secure Paystack page with M-Pesa, card or bank. You see your confirmation straight away.</li>
                  <li>Our team then contacts you on WhatsApp within 24 hours to arrange delivery or collection.</li>
                </ol>
                <span>Need help? WhatsApp us on +254 110 563 185.</span>
              </div>
            )}
            {activeTab === 'products' && (
              loadingProducts ? <SkeletonGrid count={8} /> : filteredProducts.length === 0 ? (
                <div className="mk-grid">
                  <EmptyState
                    icon={Package}
                    title={products.length === 0 ? 'Products coming soon' : 'No products found'}
                    subtitle={products.length === 0 ? "We're stocking the shelves — check back shortly." : 'Try a different category or search term.'}
                  />
                </div>
              ) : (
                <div className="mk-grid">
                  {filteredProducts.map((p) => (
                    <MarketCard
                      key={p.id}
                      item={p}
                      badge={null}
                      onOpen={() => openItem('product', p)}
                      onQuickAdd={() => {
                        if (p.hasOptions || (p.hasTieredPricing && p.pricingTiers?.length)) return openItem('product', p);
                        if (!isPurchasable(p)) return;
                        addToCart({ ...p, isPreorder: isPreorderItem(p) }, 'product', getMinOrderQty(p));
                      }}
                    />
                  ))}
                </div>
              )
            )}

            {activeTab === 'livestock' && (
              loadingLivestock ? <SkeletonGrid count={8} /> : filteredLivestock.length === 0 ? (
                <div className="mk-grid">
                  <EmptyState
                    icon={PawPrint}
                    title={livestock.length === 0 ? 'New animals coming soon' : 'No livestock found'}
                    subtitle={livestock.length === 0 ? 'Check back soon — new animals are added regularly.' : 'Try a different category or search term.'}
                  />
                </div>
              ) : (
                <div className="mk-grid">
                  {filteredLivestock.map((a) => (
                    <MarketCard
                      key={a.id}
                      item={{ ...a, unit: 'per animal' }}
                      badge={`${a.stock || 0} left`}
                      onOpen={() => openItem('livestock', a)}
                      onQuickAdd={() => {
                        if (a.hasTieredPricing && a.pricingTiers?.length) return openItem('livestock', a);
                        if (!isPurchasable(a)) return;
                        addToCart({ ...a, isPreorder: isPreorderItem(a) }, 'livestock', getMinOrderQty(a));
                      }}
                    />
                  ))}
                </div>
              )
            )}

            {activeTab === 'services' && (
              loadingServices ? (
                <div className="mk-service-grid">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="mk-skel-item">
                      <div className="mk-skel-img" style={{ aspectRatio: '16/10' }} />
                      <div className="mk-skel-lines">
                        <div className="mk-skel-line" style={{ width: '80%' }} />
                        <div className="mk-skel-line" style={{ width: '55%', marginTop: 8 }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredServices.length === 0 ? (
                <div className="mk-service-grid">
                  <EmptyState
                    icon={Wrench}
                    title={services.length === 0 ? 'Services coming soon' : 'No services found'}
                    subtitle={services.length === 0 ? `In the meantime, reach us directly on ${CONTACT.phoneDisplay} or ${CONTACT.email}.` : 'Try a different category.'}
                  />
                </div>
              ) : (
                <div className="mk-service-grid">
                  {filteredServices.map((s) => <ServiceCard key={s.id} service={s} onOpen={() => openItem('service', s)} />)}
                </div>
              )
            )}

            {activeTab === 'events' && (
              loadingEvents ? (
                <div className="mk-service-grid">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="mk-skel-item">
                      <div className="mk-skel-img" style={{ aspectRatio: '16/10' }} />
                      <div className="mk-skel-lines">
                        <div className="mk-skel-line" style={{ width: '80%' }} />
                        <div className="mk-skel-line" style={{ width: '55%', marginTop: 8 }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredEvents.length === 0 ? (
                <div className="mk-service-grid">
                  <EmptyState
                    icon={Calendar}
                    title={events.length === 0 ? 'No events scheduled yet' : 'No events found'}
                    subtitle={events.length === 0 ? 'Check back soon — new events are announced regularly.' : 'Try a different category.'}
                  />
                </div>
              ) : (
                <div className="mk-service-grid">
                  {filteredEvents.map((e) => <EventCard key={e.id} event={e} onOpen={() => openItem('event', e)} />)}
                </div>
              )
            )}
          </>
        )}
      </div>

      {/* ══ floating cart, bottom-left, always hovering over the content ══ */}
      <FloatingCart totalItems={totalItems} totalPrice={totalPrice} onOpen={() => setShowCart(true)} hidden={nearFooter || showCart || showCheckout || isDetailOpen} />

      {/* ══ remaining overlays: cart + checkout + return status — item views
           are full pages now ══ */}
      <AnimatePresence>
        {showCart && (
          <CartModal
            cart={cart}
            updateQuantity={updateQuantity}
            removeFromCart={removeFromCart}
            onClose={() => setShowCart(false)}
            totalPrice={totalPrice}
            onCheckout={() => { setShowCart(false); setShowCheckout(true); }}
          />
        )}
        {showCheckout && (
          <CheckoutModal
            cart={cart}
            totalPrice={totalPrice}
            onClose={() => setShowCheckout(false)}
            onBack={() => { setShowCheckout(false); setShowCart(true); }}
          />
        )}
        {returnStatus && (
          <ReturnStatusModal
            status={returnStatus}
            order={returnOrder}
            reference={returnReference}
            onClose={() => { setReturnStatus(null); setReturnOrder(null); setReturnReference(null); }}
          />
        )}
      </AnimatePresence>

      <Toast message={toast} />
    </div>
  );
};

export default Shop;